/**
 * Save editor overlay: opens on one slot from the archive, edits the scalar
 * territory the codec owns, and leaves everything else alone.
 *
 * The shape here follows docs/design/save-editor.md: summary first, tabs that
 * match the file's own grain, the change list visible before anything is
 * written, and a duplicate of the slot taken on the way in to the write.
 *
 * ## Two modes, and why engine mode is the default
 *
 * The first version of this panel let you write any number into any field and
 * then explained, in a warning, what the engine would do about it afterwards.
 * That is honest and it is also backwards: the player has to learn the engine's
 * rules in order to predict their own edit. The owner's verdict was "I'd rather
 * have it work exactly like the engine", and that is what **engine mode** is.
 *
 *   - **Engine mode (default).** Editing a SPECIAL stat recomputes the derived
 *     stats on the spot, exactly as `critterSetBaseStat` does, so there is no
 *     stale-derived state to warn about. Derived stats become computed readouts.
 *     Level, experience, skill points and perk slots move together on the
 *     engine's own rules. Skill points are spent at the engine's cost curve.
 *     Perks are offered only when the character qualifies.
 *   - **Expert mode.** The old free-edit behaviour, with the warning system, for
 *     writing values the game would never produce. Labelled as exactly that.
 *
 * The mode lives on the panel, not on the codec: `SaveEditor` has both the plain
 * setters and the linked ones, and this file picks.
 *
 * ## Two mechanisms, one Write
 *
 * Engine-rules character fields and inventory go through the engine mailbox;
 * Expert/raw fields go through the surgical codec. The panel batches:
 *
 *   1. Every semantic or inventory action **queues** an operation and shows it in the
 *      change list. Nothing reaches the engine on the click — the bridge is a
 *      command mailbox that runs a whole batch on the engine's own tick, so a
 *      batch per click would be both slower and pointless. It would also spend
 *      the engine on the first one: the bridge leaves it holding a loaded game it
 *      cannot hand back, and the only route out is a reload.
 *   2. Write takes **one** backup of the slot, for the whole write, whichever
 *      mechanisms it ends up using.
 *   3. Write then submits **one** batch: load, every queued item, write. The
 *      engine stops at the first failure and rolls nothing back, so any failure
 *      restores the slot from that backup here.
 *   4. Expert scalar edits, when combined with inventory, are applied **after**
 *      the round trip to the SAVE.DAT the engine wrote. Engine mode never
 *      replays its TypeScript preview over engine output.
 *   5. The engine is spent. The panel says so and restarts the reactor.
 *
 * ## Focus across re-renders
 *
 * `render()` rebuilds the body from scratch, which is fine for a grid of numbers
 * and fatal for a text box: the node the player is typing into is replaced, and
 * the keystrokes between the first one and the re-render go nowhere. That is the
 * Globals search bug, and it fires whenever an async install read lands or any
 * other field commits. The fix is `persistentInput` — the search boxes are
 * created once per panel and re-appended rather than rebuilt, and focus and
 * caret are restored after every render.
 */


import type { EmFS } from '../engine';
import {
  downloadBlob,
  exportSlot,
  listSlots,
  queueSaveMutation,
  restoreSlotSnapshot,
  slotPath,
  snapshotSlot,
} from '../saves';
import {
  PC_STAT_BOUNDS,
  PC_STAT_NAMES,
  PRIMARY_STAT_COUNT,
  SKILL_NAMES,
  STAT_NAMES,
  statIndex,
} from '../saveedit/constants';
import { forGame, type GameConstants } from '../saveedit/constants-fo1';
import {
  SaveParseError,
  characterEditorPerksOwed,
  headerVersionIsSupported,
  parseSaveDat,
  serializeSaveDat,
  type SaveDat,
} from '../saveedit/savedat';
import {
  DERIVED_BASE_STATS,
  SaveEditor,
  computeDerivedBaseStats,
  addictionState,
  computeDerivedDisplay,
  computeDerivedDisplayBreakdown,
  computeSkillValueDetailed,
  countPerksTaken,
  levelPlan,
  offeredPerks,
  perkContextFor,
  summarize,
  type EditWarning,
  type DerivedDisplayBreakdown,
  type DerivedInputs,
  type SkillValueBreakdown,
  type LevelPlan,
} from '../saveedit/edits';
import {
  maxSelectablePerks,
  skillDisplayCap,
  ADDICTIONS,
  addictionChance,
  perkAvailability,
  perkMaxRank,
  radiationLevelFor,
  type PerkAvailability,
  type PerkRequirement,
} from '../saveedit/rules';
import {
  GVAR_EXPLANATION,
  HIT_POINTS_EXPLANATION,
  SKILL_EXPLANATION,
  levelExplanationFor,
  perkExplanationFor,
  skillExplanationFor,
  traitExplanationFor,
  derivedExplanation,
  pcStatExplanation,
  poisonExplanation,
  radiationExplanation,
  skillUseExplanation,
  statExplanation,
  type Explanation,
} from '../saveedit/explain';
import {
  conditionField,
  derivedField,
  linkedFields,
  pcField,
  skillField,
  skillStatCodes,
  statField,
  type FieldKey,
} from '../saveedit/linkage';
import { gvarIndexByName, makeGvarNamer } from '../saveedit/gvars';
import {
  equippedSlot,
  readInventory,
  stackInventory,
  type InventoryEntry,
  type InventoryRead,
  type InventoryStack,
} from '../saveedit/inventory';
import {
  EQUIP_SLOT_ARMOR,
  EQUIP_SLOT_LEFT_HAND,
  EQUIP_SLOT_NAMES,
  EQUIP_SLOT_RIGHT_HAND,
  type BridgeOp,
  type BridgeInventorySnapshot,
  type EquipSlot,
  type SaveEditBridge,
} from '../saveedit/bridge';
import { installData, type CatalogItem, type ChargenEntry, type DrugProtoData, type InstallData } from '../gamedata';
import { CALIBER_TYPES, DAMAGE_TYPES, ITEM_TYPES, type ItemTypeName } from '../mods/fields';
import { itemDetail } from '../saveedit/itemdetail';
import { WEAPON_ANIMATION_LABELS, wieldArtPath } from '../saveedit/wieldable';
import { $, confirmDanger, toast } from './dom';

/**
 * Whether inventory editing can run right now, and — when it cannot — the
 * reason in words the player can act on. The shell re-evaluates this on every
 * render rather than once at mount: the engine is not booted when the panel is
 * built, and it becomes spent while the panel is open.
 */
export type BridgeStatus =
  | { bridge: SaveEditBridge; reason: null }
  | { bridge: null; reason: string };

export interface SaveEditorHost {
  /** The booted engine's filesystem, or null. */
  getFS(): EmFS | null;
  /** Re-evaluated on every render — see `BridgeStatus`. */
  bridgeStatus(): BridgeStatus;
  /**
   * Latch the engine as spent. Called before the *first* bridge call, not after
   * the last: a failed load has already moved engine state, and an engine that
   * half-ran a round trip is no more playable than one that finished it.
   */
  markEngineSpent(): void;
  /** The shell's only route back to a playable engine. HUD Quit does the same. */
  restart(): void;
  /** Push shell-side filesystem writes into IDBFS. Must settle before a restart. */
  persist(): Promise<void>;
  /** The slot list on disk changed. */
  onMutate(): void;
}

export interface SaveEditorPanel {
  open(slot: number): void;
  /** Closes unconditionally, discarding anything unwritten. */
  close(): void;
  /**
   * What every *player-initiated* close must call. `close()` throws pending
   * edits away without asking, so any route out of the panel that skips this —
   * the close button, the backdrop, Escape — is a silent data-loss bug.
   */
  requestClose(): void;
  readonly isOpen: boolean;
}

const TABS = ['Character', 'Skills', 'Perks & Traits', 'Kills', 'Globals', 'Inventory'] as const;
type Tab = (typeof TABS)[number];

/** Engine rules on, or off. Engine mode is the default and the safe one. */
type Mode = 'engine' | 'expert';

/** How many globals to render at once. 695 rows of input is not a list anyone reads. */
const GVAR_PAGE = 200;

/** How many catalog rows to render at once. The rest are behind the search box. */
const CATALOG_PAGE = 80;

/**
 * Ways to order the catalog, and which item types each one is worth offering.
 *
 * Every key reads a field the catalog already decoded (`CatalogItem.fields`,
 * straight out of `mods/proto-codec`), so a sort never re-reads a proto and
 * never computes a stat the file does not state. `types: null` means "any
 * type" — those four are the ones every proto carries.
 *
 * `descending` is the direction the sort *opens* in, not a lock: "max damage"
 * is a question about the best weapon and "name" is a question about finding
 * one, so they want opposite defaults. The toggle beside the dropdown flips
 * whichever one is chosen.
 *
 * Armour is the one type whose headline stat is not a number but seven of them:
 * DT and DR are per `DamageType`, and "the best armour" is a different list
 * against a laser than against a plasma bolt. Rather than eight DR sorts in the
 * dropdown, there are two — DR and DT — and a damage-type selector beside them
 * that says which column they read. `CatalogSortContext` is how that selection
 * reaches the value function, which is otherwise pure over the proto.
 */
type CatalogSortKey =
  | 'name' | 'pid' | 'weight' | 'value'
  | 'maxDamage' | 'minDamage' | 'armorClass' | 'caliber'
  | 'damageResistance' | 'damageThreshold';

/** What a sort needs to know beyond the item itself. */
interface CatalogSortContext {
  /** `DAMAGE_TYPES` index the armour DR/DT sorts read. Never -1: a sort must pick one. */
  armorDamageType: number;
}

interface CatalogSort {
  key: CatalogSortKey;
  label: string;
  /** Which types offer it; null for all of them. */
  types: readonly ItemTypeName[] | null;
  descending: boolean;
  /** The number to order by, or null to fall back to the name. */
  value: ((item: CatalogItem, ctx: CatalogSortContext) => number) | null;
  /**
   * Break ties by pid rather than by name. True for the sorts whose ties are a
   * *family* — every .44 weapon, every armour with the same DT — because a
   * family reads better in file order than alphabetically.
   */
  tiePid?: boolean;
}

/** The armour DR/DT field name for a `DAMAGE_TYPES` index, clamped to a real one. */
const armorDamageField = (prefix: 'damageResistance' | 'damageThreshold', type: number): string =>
  `${prefix}.${DAMAGE_TYPES[type] ?? DAMAGE_TYPES[0]}`;

const CATALOG_SORTS: readonly CatalogSort[] = [
  { key: 'name', label: 'name', types: null, descending: false, value: null },
  { key: 'pid', label: 'pid', types: null, descending: false, value: (i) => i.pid },
  { key: 'weight', label: 'weight', types: null, descending: false, value: (i) => i.fields['weight'] ?? 0 },
  { key: 'value', label: 'value', types: null, descending: true, value: (i) => i.fields['cost'] ?? 0 },
  { key: 'maxDamage', label: 'max damage', types: ['weapon'], descending: true, value: (i) => i.fields['maxDamage'] ?? 0 },
  { key: 'minDamage', label: 'min damage', types: ['weapon'], descending: true, value: (i) => i.fields['minDamage'] ?? 0 },
  { key: 'armorClass', label: 'AC', types: ['armor'], descending: true, value: (i) => i.fields['armorClass'] ?? 0 },
  {
    key: 'damageThreshold',
    label: 'DT',
    types: ['armor'],
    descending: true,
    tiePid: true,
    value: (i, ctx) => i.fields[armorDamageField('damageThreshold', ctx.armorDamageType)] ?? 0,
  },
  {
    key: 'damageResistance',
    label: 'DR',
    types: ['armor'],
    descending: true,
    tiePid: true,
    value: (i, ctx) => i.fields[armorDamageField('damageResistance', ctx.armorDamageType)] ?? 0,
  },
  { key: 'caliber', label: 'caliber', types: ['ammo', 'weapon'], descending: false, tiePid: true, value: (i) => i.fields['caliber'] ?? 0 },
];

/** The sorts on offer for a type selection, in the order they are listed. */
const sortsFor = (type: 'all' | ItemTypeName): readonly CatalogSort[] =>
  CATALOG_SORTS.filter((sort) => sort.types === null || (type !== 'all' && sort.types.includes(type)));

/** The art tile every item row gets, in CSS pixels. */
const ART_BOX_W = 56;
const ART_BOX_H = 40;

/** Perk, trait and skill icons are square-ish and smaller than an item tile. */
const ICON_BOX = 40;

/**
 * How long the "restarting" notice stays up before the reload. Long enough to
 * be read, short enough that nobody wonders whether it hung.
 */
const RESTART_DELAY_MS = 2200;

/**
 * What the Globals tab says while the install is still being read. A stable
 * string, because it is also the hook a test waits on.
 */
const LOADING_NAMES = "Reading this install's data/vault13.gam…";

/** One queued inventory operation, applied at Write and not before. */
type PendingItem =
  | { op: 'give' | 'remove'; owner: number; pid: number; count: number }
  /** An equipped item swapped for another, as one unit in the change list. */
  | { op: 'exchange'; equipSlot: EquipSlot; fromPid: number; toPid: number };

type Session = {
  slot: number;
  save: SaveDat;
  editor: SaveEditor;
  /** Queued inventory operations, in the order they will be sent to the engine. */
  queue: PendingItem[];
  /** Semantic scalar operations which Engine mode commits inside the engine. */
  engineOps: BridgeOp[];
  /** Set after the engine has loaded this slot to expose active party inventories. */
  loadedBridge: SaveEditBridge | null;
  engineInventory: BridgeInventorySnapshot | null;
  engineClaimed: boolean;
  gvarName: (index: number) => string;
  gvarSource: string;
  /**
   * The player's own install — GVAR names, item protos, item text, art, and the
   * perk/trait/skill text — resolved through the engine's own file precedence.
   * Null until the read lands, which is why every consumer of it has a "still
   * reading" branch.
   */
  install: InstallData | null;
  /** Whether the install read has finished, successfully or not. */
  installState: 'loading' | 'ready' | 'unavailable';
  /**
   * The save's current inventory, decoded read-only against those protos. Null
   * while the install is loading; a refusal when the protos and the save
   * disagree.
   */
  inventory: InventoryRead | null;
};

type Failure = {
  slot: number;
  message: string;
  offset: number | undefined;
  /** `unsupported-game` is a refusal, not a parse failure; it reads differently. */
  kind?: 'parse' | 'unsupported-game';
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function section(title: string): HTMLElement {
  const h = el('h3', 'se-h', title);
  return h;
}

function formatGameDelay(time: number, now: number): string {
  const minutes = Math.ceil(((time - now) >>> 0) / 600);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.ceil(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.ceil(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

function formatDrugPhase(drug: DrugProtoData, amounts: readonly number[]): string {
  const effects: string[] = [];
  for (let index = 0; index < 3; index++) {
    const stat = drug.stats[index]!;
    const amount = amounts[index]!;
    if (stat < 0 || amount === 0) continue;
    effects.push(`${(STAT_NAMES[stat] ?? `stat ${stat}`).replace(/_/g, ' ')} ${amount > 0 ? '+' : ''}${amount}`);
  }
  return effects.join(', ') || 'nothing';
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes % 1440 === 0) return `${minutes / 1440}d`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * Art from the install, straight from its own `.frm`.
 *
 * Returns immediately with a placeholder and fills it in when the decode lands,
 * because a decode is an archive read plus an inflate and the list must not
 * wait on 80 of them. A frame that will not decode keeps the placeholder — a
 * broken-image glyph in a list of items would read as "this item is broken".
 */
function artTile(
  install: InstallData | null,
  fid: number,
  label: string,
  boxW = ART_BOX_W,
  boxH = ART_BOX_H,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.className = 'se-art';
  canvas.width = boxW;
  canvas.height = boxH;
  canvas.title = label;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const placeholder = () => {
    canvas.classList.add('se-art-none');
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fillRect(4, 4, boxW - 8, boxH - 8);
    ctx.strokeStyle = 'rgba(255,255,255,0.16)';
    ctx.strokeRect(4.5, 4.5, boxW - 9, boxH - 9);
  };
  placeholder();
  if (!install || !fid) return canvas;

  void install.art.frameForFid(fid).then((frame) => {
    if (!frame) return;
    const source = document.createElement('canvas');
    source.width = frame.width;
    source.height = frame.height;
    const sctx = source.getContext('2d');
    if (!sctx) return;
    sctx.putImageData(new ImageData(frame.rgba, frame.width, frame.height), 0, 0);
    canvas.classList.remove('se-art-none');
    ctx.clearRect(0, 0, boxW, boxH);
    // Never magnify: inventory art is already bigger than the tile, and a
    // blown-up 12-pixel icon looks like a rendering bug.
    const scale = Math.min(boxW / frame.width, boxH / frame.height, 1);
    const w = Math.max(1, Math.round(frame.width * scale));
    const h = Math.max(1, Math.round(frame.height * scale));
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(source, Math.round((boxW - w) / 2), Math.round((boxH - h) / 2), w, h);
  });
  return canvas;
}

export function mountSaveEditor(host: SaveEditorHost): SaveEditorPanel {
  const overlay = $('#overlay-saveedit');
  const title = $('#saveedit-title');
  const body = $('#saveedit-body');
  const actionBar = $('#saveedit-actions');
  const getFS = () => host.getFS();

  // SDL owns window-level keyboard handlers while the engine is running. Let
  // form controls perform their normal browser edit, then stop the key event at
  // the editor overlay so SDL cannot swallow the character on the way up to
  // window. Do not preventDefault: text entry, selection and native steppers
  // still belong to the browser.
  const keepEditorKeysOutOfEngine = (event: KeyboardEvent) => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
      event.stopPropagation();
    }
  };
  overlay.addEventListener('keydown', keepEditorKeysOutOfEngine);
  overlay.addEventListener('keypress', keepEditorKeysOutOfEngine);
  overlay.addEventListener('keyup', keepEditorKeysOutOfEngine);

  let session: Session | null = null;
  let failure: Failure | null = null;
  let tab: Tab = 'Character';
  let focusTabAfterRender = false;
  let mode: Mode = 'engine';
  let gvarQuery = '';
  /** Inventory-tab state. Reset with the session, not with the render. */
  let catalogQuery = '';
  let catalogType: 'all' | ItemTypeName = 'all';
  /** Which of `CATALOG_SORTS` orders the list. Reset when it stops applying. */
  let catalogSort: CatalogSortKey = 'name';
  let catalogSortDescending = false;
  /** `DamageType` index, or -1 for "any". Only offered on weapons. */
  let catalogDamageType = -1;
  /**
   * Which `DamageType` column the armour DR/DT sorts read. Not a filter — it
   * hides nothing — so unlike the two above it is never -1, and `normal` is the
   * default because it is the one every armour in the game has a number for.
   */
  let catalogArmorDamageType = 0;
  /** `CaliberType` index, or -1 for "any". Offered on weapons and ammo. */
  let catalogCaliber = -1;
  /** Hide weapons the selected companion's art cannot be drawn holding. */
  let catalogWieldableOnly = false;
  let catalogScrollTop = 0;
  let catalogPid: number | null = null;
  let inventoryPid: number | null = null;
  let inventoryOwner = 0;
  /**
   * The constant tables for whichever game the open slot belongs to.
   *
   * A function rather than a value because `session` is reassigned on every
   * `load(slot)`, and the panel is rendered from closures that outlive any one
   * session. Falls back to Fallout 2 when nothing is open, which is only ever
   * read by code paths that also check `session`.
   */
  const K = (): GameConstants => forGame(session?.save.game ?? 'fo2');

  /**
   * A perk's entry from the install's own `perk.msg`, with Fallout 1's
   * description dropped.
   *
   * `gamedata/chargen.ts` reads perk names at `101 + id` and descriptions at
   * `1101 + id`, which is `perksInit` in the Fallout 2 engine. fallout1-ce's
   * `perk_init` (perk.cc:130-135) uses `101 + perk` and **`201 + perk`** — the
   * names match, the descriptions do not, and `1101 + id` in a Fallout 1
   * `perk.msg` is either missing or some other message entirely. Showing a
   * wrong-but-plausible perk description is worse than showing none, so on a
   * Fallout 1 save the name is kept and the description is dropped.
   */
  const perkEntry = (id: number) => {
    const entry = session?.install?.chargen.perks[id];
    if (!entry) return entry;
    return session?.save.game === 'fo1' ? { ...entry, description: '' } : entry;
  };

  let inventoryLoading = false;
  let inventoryLoadError: string | null = null;
  /**
   * What spent the engine, in the player's own terms, for the copy that has to
   * explain why the mode switch is locked. Null until something claims it.
   */
  let engineClaimReason: string | null = null;
  let giveCount = 1;
  let removeCount = 1;
  let grantSkillPointsCount = 1;
  /** The equipped stack an Exchange is being staged against, if any. */
  let exchangeSlot: EquipSlot | null = null;
  /** Perk picker state. */
  let perkQuery = '';
  let perkPick: number | null = null;
  /** Set once a bridge write has landed and the reload is on its way. */
  let restarting = false;

  /**
   * Whether the engine is actually reading the mailbox.
   *
   * The exports being present says the engine *has* a bridge; only a serviced
   * batch says anyone is listening, and only the main-menu loop services them.
   * So this is asked, not guessed — and asked lazily, when the Inventory tab is
   * first shown, because the answer changes with what the engine is doing and a
   * probe costs a batch.
   */
  let liveness: 'unknown' | 'checking' | 'live' | 'unserviced' = 'unknown';

  // ------------------------------------------------- focus across re-renders

  /**
   * A text box that outlives `render()`.
   *
   * `render()` calls `body.replaceChildren()`, so any input built inside it is a
   * different node every time. For a number field that is harmless — the value
   * is committed on `change` and re-read from the model. For a search box it is
   * the Globals bug: the player types, an async install read (or any other
   * field's commit) triggers a render, the node they are typing into is
   * detached, and the keystrokes since the last render are gone along with the
   * caret.
   *
   * These are created once per panel and re-appended, so the node identity, the
   * value and the selection survive. Focus does not survive detachment, so it is
   * restored explicitly at the end of `render()`.
   */
  const persistentInput = (className: string, placeholder: string, id: string): HTMLInputElement => {
    const input = el('input');
    input.type = 'search';
    input.className = className;
    input.placeholder = placeholder;
    input.dataset.persistent = id;
    return input;
  };

  const gvarSearch = persistentInput('se-search', 'Filter by name or number', 'gvar');
  const catalogSearch = persistentInput('se-search', 'Search items by name', 'catalog');
  const perkSearch = persistentInput('se-search', 'Search perks by name', 'perk');

  /** Which persistent input had focus when the render started, and where. */
  let focusRestore: { id: string; start: number | null; end: number | null } | null = null;

  const captureFocus = () => {
    const active = document.activeElement;
    if (active instanceof HTMLInputElement && active.dataset.persistent) {
      focusRestore = {
        id: active.dataset.persistent,
        start: active.selectionStart,
        end: active.selectionEnd,
      };
    } else {
      focusRestore = null;
    }
  };

  const restoreFocus = () => {
    if (!focusRestore) return;
    const input = body.querySelector<HTMLInputElement>(
      `input[data-persistent="${focusRestore.id}"]`
    );
    if (!input) return;
    input.focus();
    // A `search` input rejects setSelectionRange in some browsers; the caret
    // landing at the end is a great deal better than the focus being lost, so a
    // failure here is not worth propagating.
    try {
      if (focusRestore.start !== null) input.setSelectionRange(focusRestore.start, focusRestore.end);
    } catch {
      /* caret position is a nicety; focus is the point */
    }
  };

  const restorePanelScroll = (scrollTop: number) => {
    body.scrollTop = scrollTop;
    // Replacing the editor body can trigger scroll anchoring after layout and
    // async item art settle. Restore once now and once on the next frame so an
    // inventory double-click never throws the surrounding panel back to top.
    requestAnimationFrame(() => {
      if (overlay.classList.contains('open')) body.scrollTop = scrollTop;
    });
  };

  // ------------------------------------------------------------ field pieces

  const apply = (fn: () => void) => {
    try {
      fn();
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
    render();
  };

  const humanLabel = (value: string): string =>
    value.toLowerCase().replaceAll('_', ' ');

  const pendingChangeCount = (): number => {
    if (!session) return 0;
    const scalar = mode === 'engine'
      ? Math.max(session.editor.changes.length, session.engineOps.length)
      : session.editor.changes.length;
    return scalar + session.queue.length;
  };

  const queueAbsoluteEngineOp = (key: string, op: BridgeOp): void => {
    if (!session) return;
    const tagged = op as BridgeOp & { __key?: string };
    tagged.__key = key;
    const index = session.engineOps.findIndex((candidate) =>
      (candidate as BridgeOp & { __key?: string }).__key === key);
    // Replacing an absolute operation is still a new user action. Move it to
    // the end so dependent actions retain their most recent preview order.
    if (index >= 0) session.engineOps.splice(index, 1);
    session.engineOps.push(tagged);
  };

  const explainId = (key: string) => `se-explain-${key.replace(/[^a-z0-9_-]+/gi, '-')}`;

  /** The `?` next to a field opens a light-dismiss popover in the top layer. */
  const explainToggle = (key: string, explanation: Explanation | null): HTMLElement | null => {
    if (!explanation) return null;
    const button = el('button', 'se-info', '?');
    button.type = 'button';
    button.title = 'What this does';
    button.setAttribute('aria-label', `What ${key} does`);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', explainId(key));
    button.setAttribute('popovertarget', explainId(key));
    button.dataset.explainToggle = key;
    return button;
  };

  const explainPanel = (key: string, explanation: Explanation | null): HTMLElement | null => {
    if (!explanation) return null;
    const panel = el('div', 'se-explain');
    panel.id = explainId(key);
    panel.setAttribute('popover', 'auto');
    panel.dataset.explainFor = key;
    const head = el('div', 'se-explain-head');
    head.appendChild(el('p', 'se-explain-summary', explanation.summary));
    const close = el('button', 'se-explain-close', '×');
    close.type = 'button';
    close.title = 'Close';
    close.setAttribute('aria-label', 'Close explanation');
    close.setAttribute('popovertarget', panel.id);
    close.setAttribute('popovertargetaction', 'hide');
    head.appendChild(close);
    panel.appendChild(head);
    if (explanation.formulas?.length) {
      const formulas = el('dl', 'se-explain-formulas');
      for (const formula of explanation.formulas) {
        formulas.append(el('dt', undefined, formula.label), el('dd', undefined, formula.value));
      }
      panel.appendChild(formulas);
    }
    if (explanation.table) {
      const table = el('table', 'se-explain-table');
      const thead = el('thead');
      const header = el('tr');
      for (const column of explanation.table.columns) header.appendChild(el('th', undefined, column));
      thead.appendChild(header);
      table.appendChild(thead);
      const tbody = el('tbody');
      for (const values of explanation.table.rows) {
        const row = el('tr');
        for (const value of values) row.appendChild(el('td', undefined, value));
        tbody.appendChild(row);
      }
      table.appendChild(tbody);
      panel.appendChild(table);
    }
    const more = el('details', 'se-explain-more');
    more.appendChild(el('summary', undefined, 'More details'));
    if (explanation.detail?.length) {
      const list = el('ul');
      for (const item of explanation.detail) list.appendChild(el('li', undefined, item));
      more.appendChild(list);
    }
    more.appendChild(el('p', 'se-explain-source', explanation.source));
    panel.appendChild(more);

    const place = () => {
      if (!panel.matches(':popover-open')) return;
      const toggle = [...body.querySelectorAll<HTMLButtonElement>('button.se-info')]
        .find((candidate) => candidate.dataset.explainToggle === key);
      if (!toggle) return;

      const gutter = 12;
      panel.style.maxHeight = `${Math.max(160, Math.min(520, window.innerHeight - gutter * 2))}px`;
      const anchor = toggle.getBoundingClientRect();
      const rect = panel.getBoundingClientRect();
      const left = Math.min(
        Math.max(anchor.right - rect.width, gutter),
        Math.max(gutter, window.innerWidth - rect.width - gutter)
      );
      const below = anchor.bottom + 8;
      const above = anchor.top - rect.height - 8;
      const preferredTop = below + rect.height <= window.innerHeight - gutter ? below : above;
      const top = Math.min(
        Math.max(preferredTop, gutter),
        Math.max(gutter, window.innerHeight - rect.height - gutter)
      );
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
    };

    // Opening the disclosure changes the popover's height after its initial
    // placement. Re-measure on the next frame so it flips or clamps back into
    // the viewport; any remaining excess scrolls inside the panel.
    more.addEventListener('toggle', () => requestAnimationFrame(place));
    panel.addEventListener('toggle', () => {
      const open = panel.matches(':popover-open');
      const toggle = [...body.querySelectorAll<HTMLButtonElement>('button.se-info')]
        .find((candidate) => candidate.dataset.explainToggle === key);
      toggle?.setAttribute('aria-expanded', String(open));
      if (open) {
        place();
        window.addEventListener('resize', place);
      } else {
        window.removeEventListener('resize', place);
      }
    });
    return panel;
  };

  /**
   * One labelled field. `link` puts it in the hover graph; `explain` gives it an
   * information affordance; `readOnly` makes it a computed display, which is
   * what engine mode does to every derived stat.
   */
  const numberField = (opts: {
    label: string;
    value: number;
    commit?: (next: number) => void;
    aside?: string;
    link?: FieldKey;
    explainKey?: string;
    explanation?: Explanation | null;
    readOnly?: boolean;
    min?: number;
    max?: number;
  }): HTMLElement => {
    const wrap = el('div', 'se-field');
    if (opts.link) wrap.dataset.field = opts.link;
    if (opts.readOnly) wrap.classList.add('se-computed');

    const label = el('label', undefined, opts.label);
    wrap.appendChild(label);

    if (opts.readOnly || !opts.commit) {
      const readout = el('span', 'se-computed-value', String(opts.value));
      readout.dataset.computed = '1';
      wrap.appendChild(readout);
    } else {
      const commit = opts.commit;
      const input = el('input');
      // Native number spinners auto-repeat from a held pointer. This panel
      // re-renders after every committed edit, so Chromium can transfer that
      // held pointer to the replacement spinner and run all the way to min/max.
      // A numeric text field keeps typing and mobile keyboards without a
      // browser-owned repeating control. Keyboard arrows are handled once per
      // physical press below.
      input.type = 'text';
      input.inputMode = 'numeric';
      input.value = String(opts.value);
      const commitValue = () => {
        const parsed = Number(input.value);
        if (!Number.isFinite(parsed)) {
          toast(`${opts.label}: not a number`, 'bad');
          render();
          return;
        }
        // Bounds are part of the control, not an error the player has to
        // interpret. Typing past either end snaps to the nearest valid value;
        // keyboard arrows already use the same clamp below.
        const next = Math.max(opts.min ?? -Infinity, Math.min(opts.max ?? Infinity, parsed));
        input.value = String(next);
        apply(() => commit(next));
      };
      input.addEventListener('change', commitValue);
      input.addEventListener('keydown', (event) => {
        if ((event.key !== 'ArrowUp' && event.key !== 'ArrowDown') || event.repeat) return;
        event.preventDefault();
        const direction = event.key === 'ArrowUp' ? 1 : -1;
        const current = Number(input.value);
        const next = Math.max(opts.min ?? -Infinity, Math.min(opts.max ?? Infinity, current + direction));
        input.value = String(next);
        commitValue();
      });
      wrap.appendChild(input);
    }

    const aside = el('span', 'se-aside', opts.aside ?? '');
    const toggle = opts.explainKey ? explainToggle(opts.explainKey, opts.explanation ?? null) : null;
    if (toggle) {
      const tail = el('span', 'se-aside-row');
      tail.append(aside, toggle);
      wrap.appendChild(tail);
    } else {
      wrap.appendChild(aside);
    }

    const panel = opts.explainKey ? explainPanel(opts.explainKey, opts.explanation ?? null) : null;
    if (panel) {
      const holder = el('div', 'se-field-explain');
      holder.appendChild(panel);
      // The panel spans the whole row, so it is a sibling of the field rather
      // than a child of it — the grid would otherwise squeeze it into a column.
      const group = el('div', 'se-field-group');
      group.append(wrap, holder);
      return group;
    }
    return wrap;
  };

  const selectField = (opts: {
    label: string;
    options: { value: string; text: string }[];
    current: string;
    commit: (next: string) => void;
    aside?: string;
    explainKey?: string;
    explanation?: Explanation | null;
    disabled?: boolean;
  }): HTMLElement => {
    const wrap = el('div', 'se-field');
    wrap.appendChild(el('label', undefined, opts.label));
    const select = el('select');
    for (const o of opts.options) {
      const option = el('option', undefined, o.text);
      option.value = o.value;
      select.appendChild(option);
    }
    select.value = opts.current;
    select.disabled = opts.disabled ?? false;
    select.addEventListener('change', () => apply(() => opts.commit(select.value)));
    wrap.appendChild(select);

    const aside = el('span', 'se-aside', opts.aside ?? '');
    const toggle = opts.explainKey ? explainToggle(opts.explainKey, opts.explanation ?? null) : null;
    if (toggle) {
      const tail = el('span', 'se-aside-row');
      tail.append(aside, toggle);
      wrap.appendChild(tail);
    } else {
      wrap.appendChild(aside);
    }

    const panel = opts.explainKey ? explainPanel(opts.explainKey, opts.explanation ?? null) : null;
    if (panel) {
      const group = el('div', 'se-field-group');
      const holder = el('div', 'se-field-explain');
      holder.appendChild(panel);
      group.append(wrap, holder);
      return group;
    }
    return wrap;
  };

  const textField = (label: string, value: string, commit: (next: string) => void, maxLength: number): HTMLElement => {
    const wrap = el('div', 'se-field');
    wrap.appendChild(el('label', undefined, label));
    const input = el('input');
    input.type = 'text';
    input.value = value;
    input.maxLength = maxLength;
    input.addEventListener('change', () => apply(() => commit(input.value)));
    wrap.appendChild(input);
    return wrap;
  };

  /**
   * Warnings render where the edit that caused them lives, so `kind` alone is
   * not enough to place them: two different fields both raise `consistency`.
   * The message text is what tells them apart.
   *
   * Engine mode suppresses the derived-stale warning, because engine mode cannot
   * produce the state it warns about — the recompute has already run.
   */
  const warningsInto = (into: HTMLElement, pick: (w: EditWarning) => boolean) => {
    if (!session) return;
    for (const w of session.editor.pendingWarnings.filter(pick)) {
      if (mode === 'engine' && w.kind === 'derived-stale') continue;
      const note = el('div', 'note', w.message);
      if (w.kind === 'derived-stale') {
        const btn = el('button', 'tiny', 'Recompute now');
        btn.addEventListener('click', () => apply(() => session?.editor.recomputeDerivedStats()));
        note.append(' ', btn);
      }
      into.appendChild(note);
    }
  };

  // -------------------------------------------------------------- mode strip

  const modeStrip = (): HTMLElement => {
    const wrap = el('div', 'se-mode');
    const engineStatus = host.bridgeStatus();
    const actions = el('div', 'se-mode-actions');
    for (const [value, label] of [
      ['engine', 'Engine rules'],
      ['expert', 'Expert'],
    ] as const) {
      const btn = el('button', `tiny${mode === value ? ' primary' : ' ghost'}`, label);
      btn.type = 'button';
      btn.disabled = value === 'engine' && !!session
        && (!headerVersionIsSupported(session.save.header, session.save.game) || !engineStatus.bridge);
      if (session?.engineClaimed && value !== mode) {
        btn.disabled = true;
        // Naming the actual cause: the claim is lazy now, so "party inventory
        // inspection" is only one of the two things that can have spent it.
        btn.title = `${engineClaimReason ?? 'An engine-rules edit has loaded the save into the engine.'}`
          + ' Close the editor to restart before switching modes.';
      }
      if (value === 'engine' && !engineStatus.bridge) btn.title = engineStatus.reason ?? 'Engine bridge unavailable';
      btn.dataset.modeSet = value;
      btn.setAttribute('aria-pressed', String(mode === value));
      btn.addEventListener('click', async () => {
        if (session && (session.editor.changes.length || session.queue.length || session.engineOps.length)) {
          if (!(await confirmDanger('Switching modes discards the pending edits for this slot. Continue?'))) return;
          const slot = session.slot;
          mode = value;
          load(slot);
          render();
          return;
        }
        mode = value;
        render();
      });
      actions.appendChild(btn);
    }
    wrap.appendChild(actions);
    const pending = pendingChangeCount();
    if (pending > 0) {
      const status = el('span', 'se-pending-status', `${pending} change${pending === 1 ? '' : 's'} pending`);
      status.setAttribute('role', 'status');
      wrap.appendChild(status);
    }
    wrap.appendChild(
      el(
        'span',
        'se-mode-note',
        mode === 'engine'
          ? tab === 'Inventory'
            ? 'Inventory changes are staged until Save changes.'
            : 'Uses the game’s rules. Changes are staged until Save changes.'
          : headerVersionIsSupported(session!.save.header, session!.save.game)
            ? 'Raw repair mode writes stored fields directly. It can create states normal gameplay cannot.'
            : 'This save version is not accepted by the engine. Only raw repair/export is available; write a copy and keep the original.'
      )
    );
    if (mode === 'expert') wrap.classList.add('se-mode-expert');
    return wrap;
  };

  // -------------------------------------------------------------------- tabs

  const characterTab = (into: HTMLElement) => {
    if (!session) return;
    const { save, editor } = session;
    const shown = summarize(save).special;
    const plan = levelPlan(save);
    const chargen = session.install?.chargen;

    into.appendChild(section('Identity'));
    const identity = el('div', 'se-fields');
    identity.append(
      textField('character name', save.header.characterName, (value) => editor.setCharacterName(value), 31),
      textField('save description', save.header.description, (value) => editor.setDescription(value), 29),
    );
    into.appendChild(identity);

    into.appendChild(section('SPECIAL'));
    const special = el('div', 'se-fields');
    for (let i = 0; i < PRIMARY_STAT_COUNT; i++) {
      const name = STAT_NAMES[i]!;
      const entry = chargen?.special[i];
      special.appendChild(
        numberField({
          label: entry?.named ? entry.name : name,
          value: mode === 'engine' ? shown[name]! : save.critter.baseStats[i]!,
          commit: (v) => {
            if (mode === 'engine') {
              if (v === shown[name]) return;
              editor.setSpecialDisplayLinked(i, v);
              queueAbsoluteEngineOp(`special:${i}`, { kind: 'setSpecial', stat: i, value: v });
            } else editor.setStat(i, v);
          },
          aside: mode === 'engine' ? '' : `shown: ${shown[name]}`,
          link: statField(name),
          explainKey: `stat:${name}`,
          // The install's own description when it has one, ours when it does
          // not: the game's text says what the stat is *for*, ours says which
          // numbers it feeds, and both are worth having.
          explanation: mergeExplanation(statExplanation(name), entry),
          min: 1,
          max: 10,
        })
      );
    }
    into.appendChild(special);
    if (mode === 'expert') {
      into.appendChild(el('p', 'hint', 'Stored base values; bonuses and traits are applied separately.'));
    }
    warningsInto(into, (w) => w.kind === 'derived-stale');

    if (mode === 'expert') {
      const details = el('details', 'se-unestablished');
      details.appendChild(el('summary', undefined, 'Stored bonus stats (raw)'));
      details.appendChild(el('p', 'hint', 'These are added to base stats by critterGetStat. They are separate from trait and perk modifiers.'));
      const bonuses = el('div', 'se-fields');
      for (let i = 0; i < save.critter.bonusStats.length; i++) {
        bonuses.appendChild(numberField({
          label: STAT_NAMES[i]!,
          value: save.critter.bonusStats[i]!,
          commit: (value) => editor.setBonusStat(i, value),
        }));
      }
      details.appendChild(bonuses);
      into.appendChild(details);
    }

    into.appendChild(section('Derived stats'));
    const derived = el('div', 'se-fields');
    const inputs: DerivedInputs = { baseStats: save.critter.baseStats, bonusStats: save.critter.bonusStats, traits: save.traits, perks: save.perks.subarray(0, K().perkNames.length) };
    const expectedBases = computeDerivedBaseStats(inputs);
    for (const [name, formula] of DERIVED_BASE_STATS) {
      const index = statIndex(name);
      const stored = save.critter.baseStats[index]!;
      const display = computeDerivedDisplay(index, inputs);
      const breakdown = computeDerivedDisplayBreakdown(index, inputs);
      derived.appendChild(
        numberField({
          label: humanLabel(name),
          value: mode === 'engine' ? display : stored,
          commit: mode === 'engine' ? undefined : (v) => editor.setStat(index, v),
          readOnly: mode === 'engine',
          aside: mode === 'engine' ? '' : `${formula} · shown: ${display}`,
          link: derivedField(name),
          explainKey: `derived:${name}`,
          explanation: derivedValueExplanation(
            derivedExplanation(name),
            formula,
            expectedBases.get(name) ?? stored,
            breakdown
          ),
        })
      );
    }
    into.appendChild(derived);
    warningsInto(into, (w) => w.kind === 'engine-would-refuse');

    into.appendChild(section('Condition'));
    const condition = el('div', 'se-fields');
    const rad = radiationLevelFor(save.dude.radiation);
    condition.appendChild(
      numberField({
        label: 'hit points',
        value: save.dude.hp,
        commit: (v) => {
          editor.setHitPoints(v);
          if (mode === 'engine') queueAbsoluteEngineOp('condition:hp', { kind: 'setCondition', field: 0, value: v });
        },
        min: mode === 'engine' ? 1 : 0,
        max: mode === 'engine' ? summarize(save).maximumHitPoints : 2000,
        aside: `of ${summarize(save).maximumHitPoints}`,
        link: conditionField('hit points'),
        explainKey: 'condition:hp',
        explanation: HIT_POINTS_EXPLANATION,
      })
    );
    condition.appendChild(
      numberField({
        label: 'radiation',
        value: save.dude.radiation,
        commit: (v) => {
          editor.setRadiation(v);
          if (mode === 'engine') queueAbsoluteEngineOp('condition:radiation', { kind: 'setCondition', field: 1, value: v });
        },
        min: 0,
        max: 2000,
        aside: `${rad.name}${rad.penalties.length ? '' : ' — no penalty'}`,
        link: conditionField('radiation'),
        explainKey: 'condition:radiation',
        explanation: radiationExplanation(save.dude.radiation, save.game),
      })
    );
    condition.appendChild(
      numberField({
        label: 'poison',
        value: save.dude.poison,
        commit: (v) => {
          editor.setPoison(v);
          if (mode === 'engine') queueAbsoluteEngineOp('condition:poison', { kind: 'setCondition', field: 2, value: v });
        },
        min: 0,
        max: 2000,
        aside: save.dude.poison > 0 ? `${Math.ceil(save.dude.poison / 2)} tick(s) to clear` : 'clear',
        link: conditionField('poison'),
        explainKey: 'condition:poison',
        explanation: poisonExplanation(save.dude.poison, save.game),
      })
    );
    into.appendChild(condition);
    warningsInto(into, (w) => w.kind === 'out-of-band');

    into.appendChild(section('Addictions & withdrawal'));
    if (save.game === 'fo1') {
      // `ADDICTIONS` is a Fallout 2 registry: its item pids, its withdrawal
      // perks (Jet and Tragic cards do not exist in Fallout 1) and its stock
      // GVAR numbers are all Fallout 2's. Writing one of those numbers into a
      // Fallout 1 save would set an unrelated quest flag, which is exactly the
      // kind of quiet corruption this editor is built not to do.
      const note = el('div', 'note', 'Addiction and withdrawal editing is Fallout 2 only: the drug '
        + 'registry, its withdrawal perks and its global-variable numbers are all Fallout 2\'s, and '
        + 'writing them into a Fallout 1 save would set unrelated quest flags. Hit points, radiation '
        + 'and poison above are edited directly and work here.');
      note.dataset.fo1Addictions = 'true';
      into.appendChild(note);
      into.appendChild(section('Progress'));
      progressInto(into, plan);
      return;
    }
    const selectedTraits = new Set(Array.from(save.traits));
    const chanceOptions = {
      chemReliant: selectedTraits.has(K().traitIndex('CHEM_RELIANT')),
      chemResistant: selectedTraits.has(K().traitIndex('CHEM_RESISTANT')),
      flowerChild: (save.perks[K().perkIndex('FLOWER_CHILD')] ?? 0) > 0,
    };
    const installedGlobals = session.install?.globals ?? undefined;
    const addictionGvars = ADDICTIONS.map((rule) => {
      const named = gvarIndexByName(rule.gvarName, installedGlobals);
      if (installedGlobals) return named;
      return rule.stockGvar < save.gvars.length ? rule.stockGvar : -1;
    });
    const addictions = el('div', 'se-cards se-addictions');
    for (let index = 0; index < ADDICTIONS.length; index++) {
      const rule = ADDICTIONS[index]!;
      const gvar = addictionGvars[index]!;
      const installedDrug = session.install?.catalog.byPid.get(rule.pid)?.drug;
      const installedPerk = installedDrug
        ? installedDrug.withdrawalEffect >= 0 ? installedDrug.withdrawalEffect : null
        : rule.perk;
      const state = addictionState(save, rule, gvar, installedPerk);
      const card = el('article', 'se-card');
      card.dataset.addiction = rule.key;
      card.appendChild(el('h4', undefined, rule.name));
      const status = state.withdrawalActive
        ? 'Withdrawal active'
        : state.pendingEvent
          ? `Addicted · withdrawal in ${formatGameDelay(state.pendingEvent.time, save.header.gameTime)}`
          : state.addicted
            ? 'Addicted · no generic timer'
            : 'Not addicted';
      card.appendChild(el('p', 'hint', status));
      const effects = rule.withdrawal.length
        ? rule.withdrawal.map((effect) => `${effect.stat.replace(/_/g, ' ')} ${effect.amount}`).join(' · ')
        : 'No generic stat penalty';
      const chance = installedDrug?.addictionChance ?? rule.baseChance;
      const effectiveChance = addictionChance(chance, chanceOptions);
      const chanceText = effectiveChance === chance
        ? `${chance}% addiction chance`
        : `${chance}% base → ${effectiveChance}% for this character`;
      card.appendChild(el('p', undefined, `${chanceText} · ${effects}`));
      if (installedDrug) {
        const phases = [`now: ${formatDrugPhase(installedDrug, installedDrug.immediate)}`];
        if (installedDrug.delayed1.some((amount) => amount !== 0)) {
          phases.push(`after ${formatMinutes(installedDrug.duration1)}: ${formatDrugPhase(installedDrug, installedDrug.delayed1)}`);
        }
        if (installedDrug.delayed2.some((amount) => amount !== 0)) {
          phases.push(`after ${formatMinutes(installedDrug.duration2)}: ${formatDrugPhase(installedDrug, installedDrug.delayed2)}`);
        }
        card.appendChild(el('p', 'hint', phases.join(' · ')));
      }

      const controls = el('div', 'se-card-controls');
      if (state.addicted) {
        if (!state.withdrawalActive && installedPerk !== null && installedPerk >= 0) {
          const withdraw = el('button', 'tiny ghost', 'Start withdrawal');
          withdraw.type = 'button';
          withdraw.disabled = mode !== 'engine' || save.queueEvents === null;
          withdraw.addEventListener('click', () => apply(() => editor.setAddiction(
            rule,
            gvar,
            'withdrawal',
            addictionGvars,
            { perk: installedPerk },
          )));
          controls.appendChild(withdraw);
        }
        const clear = el('button', 'tiny ghost', 'Clear addiction');
        clear.type = 'button';
        clear.disabled = mode !== 'engine' || save.queueEvents === null;
        clear.addEventListener('click', () => apply(() => editor.setAddiction(
          rule,
          gvar,
          'clear',
          addictionGvars,
          { perk: installedPerk },
        )));
        controls.appendChild(clear);
      } else if (gvar >= 0) {
        // Beer/booze and Tragic are script-controlled in stock. Their proto has
        // zeroed generic fields, which must not turn into an immediate queue event.
        const genericOnset = rule.onsetMinutes === null
          ? null
          : installedDrug?.withdrawalOnset ?? rule.onsetMinutes;
        const add = el('button', 'tiny primary', genericOnset === null ? 'Mark addicted' : 'Add addiction');
        add.type = 'button';
        add.disabled = mode !== 'engine' || save.queueEvents === null;
        add.addEventListener('click', () => apply(() => editor.setAddiction(
          rule,
          gvar,
          genericOnset === null ? 'addicted' : 'pending',
          addictionGvars,
          { onsetMinutes: genericOnset, perk: installedPerk },
        )));
        controls.appendChild(add);
      }
      card.appendChild(controls);
      addictions.appendChild(card);
    }
    into.appendChild(addictions);
    into.appendChild(el(
      'p',
      'hint',
      mode === 'engine'
        ? 'These controls keep the addiction GVAR, HUD ADDICT bit, withdrawal perk/stat effects and saved event queue together. Values come from this install’s winning drug prototypes when available.'
        : 'Switch to Engine rules to edit addiction as a complete state. Adding an addiction perk by itself is only a penalty, not an addiction.',
    ));

    into.appendChild(section('Progress'));
    progressInto(into, plan);
  };

  /**
   * Level, experience, skill points and perk slots — the four the engine ties
   * together, shown together.
   */
  const progressInto = (into: HTMLElement, plan: LevelPlan) => {
    if (!session) return;
    const { save, editor } = session;
    const fields = el('div', 'se-fields');

    for (let i = 0; i < PC_STAT_NAMES.length; i++) {
      const name = PC_STAT_NAMES[i]!;
      const value = save.pcStats[i]!;
      let aside = '';
      let commit: (v: number) => void;

      if (name === 'LEVEL') {
        aside =
          mode === 'engine'
            ? `${plan.skillPointsPerLevel} skill points a level · perk every ${plan.levelsPerPerk}`
            : 'free';
        commit = (v) => {
          if (mode !== 'engine') return void editor.setPcStat(i, v);
          if (v === value) return;
          if (v < value) throw new Error('Engine rules does not lower a character level. Use Expert for a raw repair.');
          editor.setLevelLinked(v);
          queueAbsoluteEngineOp('experience', { kind: 'setExperience', value: save.pcStats[2]! });
        };
      } else if (name === 'EXPERIENCE') {
        aside =
          plan.band.ceiling === null
            ? `level ${plan.level} is the top`
            : `level ${plan.level} band: ${plan.band.floor}–${plan.band.ceiling}`;
        commit = (v) => {
          if (mode !== 'engine') return void editor.setExperience(v);
          if (v === value) return;
          if (v < value) throw new Error('Engine rules does not remove experience. Use Expert for a raw repair.');
          editor.setExperienceLinked(v);
          queueAbsoluteEngineOp('experience', { kind: 'setExperience', value: v });
        };
      } else if (name === 'UNSPENT_SKILL_POINTS') {
        aside = mode === 'engine' ? 'spend these on the Skills tab' : 'free';
        commit = (v) => editor.setPcStat(i, v);
      } else if (name === 'REPUTATION' || name === 'KARMA') {
        aside = mode === 'engine' ? 'set through the engine' : 'free';
        commit = (v) => {
          editor.setPcStat(i, v);
          if (mode === 'engine') queueAbsoluteEngineOp(`pc:${i}`, { kind: 'setPcStat', stat: i, value: v });
        };
      } else {
        commit = (v) => editor.setPcStat(i, v);
      }

      fields.appendChild(
        numberField({
          label: humanLabel(name),
          value,
          // In engine mode unspent skill points are the ledger the Skills tab
          // spends from, and typing a number into it is how that ledger stops
          // meaning anything. Expert mode has it.
          commit: mode === 'engine' && name === 'UNSPENT_SKILL_POINTS' ? undefined : commit,
          readOnly: mode === 'engine' && name === 'UNSPENT_SKILL_POINTS',
          aside,
          link: pcField(name),
          explainKey: `pc:${name}`,
          explanation: pcStatExplanation(name),
          min: PC_STAT_BOUNDS[i]![0],
          max: PC_STAT_BOUNDS[i]![1],
        })
      );
    }
    into.appendChild(fields);

    if (mode === 'engine') {
      const grant = quantityControl({
        label: 'Grant skill points',
        value: grantSkillPointsCount,
        max: null,
        enabled: true,
        disabledNote: '',
        onCount: (count) => { grantSkillPointsCount = count; },
        onStage: (count) => apply(() => {
          if (!session) return;
          editor.setPcStat(0, save.pcStats[0]! + count);
          session.engineOps.push({ kind: 'grantSkillPoints', count });
          grantSkillPointsCount = 1;
        }),
        testId: 'grant-skill-points',
      });
      grant.title = 'Adds directly to the unspent skill-point pool.';
      into.appendChild(grant);
    }

    // The perk-slot ledger. Engine mode keeps it true; expert mode can leave it
    // anywhere, so it is reported rather than assumed in both.
    const ledger = el('div', 'se-ledger');
    ledger.dataset.perkSlots = String(plan.perkSlotsEarned);
    const owed = plan.perksOwed;
    ledger.append(
      el('span', undefined, `Perk slots earned by level ${plan.level}: ${plan.perkSlotsEarned}`),
      el('span', undefined, `Perks held: ${plan.perksTaken} of ${maxSelectablePerks(save.game)}`),
      el('span', undefined, owed === null ? 'Perk picks owed: unknown' : `Perk picks owed: ${owed}`)
    );
    into.appendChild(ledger);

    if (plan.pendingCatchUpSkillPoints > 0) {
      const note = el('div', 'note');
      note.dataset.catchUp = String(plan.pendingCatchUpSkillPoints);
      note.textContent =
        `The character screen thinks this character is level ${plan.lastLevel}, and the file says `
        + `${plan.level}. The next time the character button is pressed in game, the level-up loop will `
        + `catch up and hand over ${plan.pendingCatchUpSkillPoints} skill point(s). `
        + (mode === 'engine'
          ? 'Setting the level here moves that counter with it, so this only appears for a gap something else left behind.'
          : 'Expert mode does not move that counter — raising the level here and adding skill points yourself gets them twice.');
      into.appendChild(note);
    }

    into.appendChild(explainPanel('pc:levelling', levelExplanationFor(save.game)) ?? el('span'));
    warningsInto(into, (w) => w.kind === 'consistency' && w.message.startsWith('Experience'));
  };

  const skillsTab = (into: HTMLElement) => {
    if (!session) return;
    const { save, editor } = session;
    const plan = levelPlan(save);
    const chargen = session.install?.chargen;

    const head = el('div', 'se-skills-head');
    head.append(
      el(
        'span',
        'se-skills-unspent',
        `${save.pcStats[0]} point${save.pcStats[0] === 1 ? '' : 's'} available`
      ),
      el('span', 'se-skills-points', `${plan.skillPointsPerLevel} per level`)
    );
    const toggle = explainToggle('skills:overview', skillExplanationFor(save.game));
    if (toggle) head.appendChild(toggle);
    into.appendChild(head);
    const overview = explainPanel('skills:overview', skillExplanationFor(save.game));
    if (overview) into.appendChild(overview);

    into.appendChild(section('Tagged skills'));
    const tagOptions = [{ value: '-1', text: '— unused —' }].concat(
      SKILL_NAMES.map((name, index) => ({ value: String(index), text: name }))
    );
    const tags = el('div', 'se-fields');
    for (let slot = 0; slot < save.taggedSkills.length; slot++) {
      tags.appendChild(selectField({
        label: slot === 3 ? 'Tag! perk slot' : `tag slot ${slot + 1}`,
        options: tagOptions,
        current: String(save.taggedSkills[slot]),
        commit: (value) => editor.tagSkill(slot, Number(value) < 0 ? null : Number(value)),
        disabled: mode === 'engine',
        aside: mode === 'engine' ? '' : 'raw field',
      }));
    }
    into.appendChild(tags);

    const fields = el('div', 'se-fields se-skill-list');
    for (let i = 0; i < SKILL_NAMES.length; i++) {
      const tagged = Array.from(save.taggedSkills).includes(i);
      const detail = computeSkillValueDetailed(save, i);
      const entry = chargen?.skills[i];
      const attributes = chargen?.skillAttributes[i] || skillStatCodes(i);

      // The owner's complaint, answered: at the cap, say so and say what the
      // formula actually produced, so 255 points and 280 points stop looking
      // like the same character.
      const aside = detail.capped
        ? `${detail.value}% (capped — formula gives ${detail.uncapped}%)`
        : `${detail.value}%`;

      const row = el('div', 'se-skill-row');
      row.dataset.skill = SKILL_NAMES[i]!;
      row.dataset.uncapped = String(detail.uncapped);
      if (detail.capped) row.dataset.capped = '1';

      if (mode === 'engine') {
        // Engine mode spends the pool at the engine's cost curve, so the control
        // is a pair of steppers and a price, not a raw points box. The raw
        // points are still shown — they are what the file holds.
        const plan1 = editor.skillSpendPlan(i, 1);
        const price = plan1.refusal ? null : plan1.cost;
        row.appendChild(
          numberField({
            label: `${entry?.named ? entry.name : SKILL_NAMES[i]}${tagged ? ' ★' : ''}`,
            value: detail.value,
            readOnly: true,
            aside: '',
            link: skillField(SKILL_NAMES[i]!),
            explainKey: `skill:${SKILL_NAMES[i]}`,
            explanation: skillValueExplanation(SKILL_NAMES[i]!, detail, entry),
          })
        );
        const stepper = el('div', 'se-stepper');
        for (const delta of [-1, 1] as const) {
          const p = editor.skillSpendPlan(i, delta);
          const refundable = delta < 0 && session.engineOps.some((op) => op.kind === 'addSkill' && op.skill === i);
          const btn = el('button', 'tiny ghost', delta > 0 ? '+1%' : '−1%');
          btn.type = 'button';
          btn.dataset.skillStep = String(delta);
          btn.disabled = delta < 0 ? !refundable : p.refusal !== null;
          if (delta < 0) btn.title = refundable
            ? 'Refund the most recent point bought for this skill in this editor session.'
            : 'Only points bought during this editor session can be refunded.';
          if (delta > 0 && p.refusal) btn.title = p.refusal;
          btn.addEventListener('click', () => apply(() => {
            if (delta < 0) {
              if (!session) return;
              let stagedIndex = -1;
              for (let index = session.engineOps.length - 1; index >= 0; index--) {
                const op = session.engineOps[index]!;
                if (op.kind === 'addSkill' && op.skill === i) {
                  stagedIndex = index;
                  break;
                }
              }
              if (stagedIndex < 0) throw new Error('Only points bought during this editor session can be refunded.');
              editor.spendSkillPoints(i, -1);
              session.engineOps.splice(stagedIndex, 1);
              return;
            }
            editor.spendSkillPoints(i, delta);
            session?.engineOps.push({ kind: 'addSkill', skill: i });
          }));
          stepper.appendChild(btn);
        }
        stepper.appendChild(el('span', 'se-aside', price === null ? '' : `${price} point${price === 1 ? '' : 's'}`));
        row.appendChild(stepper);
      } else {
        row.appendChild(
          numberField({
            label: `${entry?.named ? entry.name : SKILL_NAMES[i]}${tagged ? ' ★' : ''}`,
            value: save.critter.skills[i]!,
            commit: (v) => editor.setSkillPoints(i, v),
            aside: `${aside}${attributes ? ` · ${attributes}` : ''}`,
            link: skillField(SKILL_NAMES[i]!),
            explainKey: `skill:${SKILL_NAMES[i]}`,
            explanation: skillValueExplanation(SKILL_NAMES[i]!, detail, entry),
            min: 0,
            max: skillDisplayCap(save.game),
          })
        );
      }
      fields.appendChild(row);
    }
    into.appendChild(fields);
  };

  const perksTab = (into: HTMLElement) => {
    if (!session) return;
    const { save, editor } = session;
    const chargen = session.install?.chargen;
    const plan = levelPlan(save);

    // --- traits ------------------------------------------------------------
    into.appendChild(section('Traits'));
    const traitToggle = explainToggle('traits:overview', traitExplanationFor(save.game));
    const traitHead = el('div', 'se-skills-head');
    traitHead.appendChild(el('span', undefined, 'Two maximum, the same as character creation.'));
    if (traitToggle) traitHead.appendChild(traitToggle);
    into.appendChild(traitHead);
    const traitOverview = explainPanel('traits:overview', traitExplanationFor(save.game));
    if (traitOverview) into.appendChild(traitOverview);

    const traitOptions = [{ value: '-1', text: '— none —' }].concat(
      K().traitNames.map((n, i) => ({ value: String(i), text: chargen?.traits[i]?.name ?? n }))
    );
    const traits = el('div', 'se-fields');
    for (let slot = 0; slot < save.traits.length; slot++) {
      const current = save.traits[slot]!;
      const entry = current >= 0 ? chargen?.traits[current] : undefined;
      traits.appendChild(
        selectField({
          label: `slot ${slot + 1}`,
          options: traitOptions,
          current: String(current),
          commit: (v) => {
            const next = Number(v) < 0 ? null : Number(v);
            return mode === 'engine' ? editor.setTraitLinked(slot, next) : editor.setTrait(slot, next);
          },
          disabled: mode === 'engine',
          aside: entry?.description ? entry.description.split(/(?<=\.)\s/)[0] ?? '' : '',
          explainKey: `trait:slot${slot}`,
          explanation: entry
            ? { summary: entry.description || traitExplanationFor(save.game).summary, source: chargenSource(session) }
            : traitExplanationFor(save.game),
        })
      );
    }
    into.appendChild(traits);
    // Chosen traits get their icon and the game's own words, which is the whole
    // point of reading trait.msg — a name in an enum is not a description.
    const chosen = el('div', 'se-cards');
    for (const slot of [0, 1]) {
      const id = save.traits[slot]!;
      if (id < 0) continue;
      const entry = chargen?.traits[id];
      chosen.appendChild(cardFor(entry, K().traitNames[id]!, entry?.description ?? ''));
    }
    if (chosen.childElementCount) into.appendChild(chosen);
    warningsInto(into, (w) => w.kind === 'consistency' && w.message.startsWith('Traits'));

    // --- perks held --------------------------------------------------------
    into.appendChild(section('Perks'));
    const perkHead = el('div', 'se-skills-head');
    perkHead.appendChild(
      el(
        'span',
        undefined,
        `${plan.perksTaken} held · ${plan.perkSlotsEarned} slot(s) earned by level ${plan.level}`
      )
    );
    const perkToggle = explainToggle('perks:overview', perkExplanationFor(save.game));
    if (perkToggle) perkHead.appendChild(perkToggle);
    into.appendChild(perkHead);
    const perkOverview = explainPanel('perks:overview', perkExplanationFor(save.game));
    if (perkOverview) into.appendChild(perkOverview);

    const taken = el('div', 'se-cards');
    let any = false;
    for (let i = 0; i < K().perkNames.length; i++) {
      const rank = save.perks[i]!;
      if (rank <= 0) continue;
      any = true;
      const entry = perkEntry(i);
      const card = cardFor(entry, K().perkNames[i]!, entry?.description ?? '', rank);
      card.dataset.perk = K().perkNames[i]!;
      const controls = el('div', 'se-card-controls');
      if (mode === 'expert') {
        const max = perkMaxRank(i);
        const up = el('button', 'tiny ghost', '+');
        up.type = 'button';
        up.disabled = max !== -1 && rank >= max;
        up.addEventListener('click', () => apply(() => editor.setPerkRank(i, rank + 1)));
        const down = el('button', 'tiny ghost', '−');
        down.type = 'button';
        down.addEventListener('click', () => apply(() => editor.setPerkRank(i, rank - 1)));
        controls.append(down, up);
      }
      card.appendChild(controls);
      taken.appendChild(card);
    }
    if (!any) taken.appendChild(el('p', 'hint', 'No perks taken.'));
    into.appendChild(taken);

    // --- picker ------------------------------------------------------------
    into.appendChild(section('Add a perk'));
    perkPickerInto(into);

    if (save.partySlotCount > 1) {
      into.appendChild(section('Party-slot perk rows'));
      into.appendChild(el(
        'p',
        'hint',
        mode === 'engine'
          ? `${save.partySlotCount - 1} serialized party rows are shown read-only; the engine manages companion perks.`
          : 'Party names are not stored in SAVE.DAT, so rows are identified by their party.txt slot number.',
      ));
      for (let party = 1; party < save.partySlotCount; party++) {
        const details = el('details', 'se-unestablished');
        const held: number[] = [];
        for (let perk = 0; perk < K().perkNames.length; perk++) {
          if ((save.perks[party * K().perkNames.length + perk] ?? 0) > 0) held.push(perk);
        }
        details.appendChild(el('summary', undefined, `party slot ${party} · ${held.length} perk(s)`));
        const rows = el('div', 'se-fields');
        for (const perk of held) {
          const rank = save.perks[party * K().perkNames.length + perk]!;
          rows.appendChild(numberField({
            label: K().perkNames[perk]!,
            value: rank,
            commit: mode === 'engine' ? undefined : (value) => editor.setPerkRank(perk, value, party),
            readOnly: mode === 'engine',
            min: 0,
            max: perkMaxRank(perk) === -1 ? undefined : perkMaxRank(perk),
          }));
        }
        if (!held.length) rows.appendChild(el('p', 'hint', 'No perks in this row.'));
        details.appendChild(rows);
        if (mode === 'expert') {
          const controls = el('div', 'se-side-controls');
          const select = el('select');
          for (let perk = 0; perk < K().perkNames.length; perk++) {
            const option = el('option', undefined, perkEntry(perk)?.name ?? K().perkNames[perk]!);
            option.value = String(perk);
            select.appendChild(option);
          }
          const add = el('button', 'tiny primary', 'Add one rank');
          add.type = 'button';
          add.addEventListener('click', () => apply(() => editor.addPerk(Number(select.value), party)));
          controls.append(select, add);
          details.appendChild(controls);
        }
        into.appendChild(details);
      }
    }
  };

  /**
   * The picker.
   *
   * Engine mode lists only what `perkCanAddAtLevel` would offer; expert mode
   * lists everything, including the perks the game never offers at all —
   * addictions, armour bonuses, the GAIN_ perks — with each unmet requirement
   * flagged. Either way the requirements are written out rather than implied,
   * because "this perk is greyed out" is not an answer to "why".
   */
  const perkPickerInto = (into: HTMLElement) => {
    if (!session) return;
    const { save, editor } = session;
    const all = offeredPerks(save);
    const list = mode === 'engine' ? all.filter((p) => p.available) : allPerksForExpert(save);
    const owedPick = levelPlan(save).perksOwed ?? 0;

    perkSearch.value = perkQuery;
    const controls = el('div', 'se-side-controls');
    controls.appendChild(perkSearch);
    into.appendChild(controls);

    const q = perkQuery.trim().toLowerCase();
    const named = (p: PerkAvailability) => perkEntry(p.perk)?.name ?? K().perkNames[p.perk]!;
    const matches = list.filter((p) => !q || named(p).toLowerCase().includes(q) || K().perkNames[p.perk]!.toLowerCase().includes(q));

    const count = el('p', 'hint', '');
    count.dataset.perkCount = String(matches.length);
    count.textContent =
      mode === 'engine'
        ? owedPick > 0
          ? `${matches.length} perk(s) this character qualifies for right now · ${owedPick} pick(s) owed.`
          : `${matches.length} perk(s) qualify, but no perk pick is currently owed.`
        : `${matches.length} perk(s), every one the file can hold. Requirements are shown but not enforced.`;
    into.appendChild(count);

    if (mode === 'engine' && countPerksTaken(save) >= maxSelectablePerks(save.game)) {
      const note = el('div', 'note');
      note.textContent =
        `This character already holds ${maxSelectablePerks(save.game)} distinct perks, which is where the game's own `
        + 'perk dialog stops offering anything (character_editor.cc kMaxSelectablePerks).';
      into.appendChild(note);
    }

    const grid = el('div', 'se-cards se-perk-picker');
    for (const availability of matches.slice(0, CATALOG_PAGE)) {
      const entry = perkEntry(availability.perk);
      const card = cardFor(entry, K().perkNames[availability.perk]!, entry?.description ?? '');
      card.dataset.perkOption = K().perkNames[availability.perk]!;
      card.dataset.available = String(availability.available);
      if (!availability.available) card.classList.add('se-unmet');
      if (perkPick === availability.perk) card.classList.add('sel');

      const reqs = el('ul', 'se-reqs');
      for (const requirement of availability.requirements) {
        const li = el('li', requirement.kind === 'not-selectable' || requirement.met ? 'ok' : 'unmet');
        li.textContent = requirementText(requirement);
        reqs.appendChild(li);
      }
      card.appendChild(reqs);

      const add = el('button', 'tiny primary', 'Take this perk');
      add.type = 'button';
      add.dataset.perkAdd = K().perkNames[availability.perk]!;
      const needsFollowup = mode === 'engine'
        && (K().perkNames[availability.perk] === 'TAG' || K().perkNames[availability.perk] === 'MUTATE');
      add.disabled = mode === 'engine' && (!availability.available || needsFollowup || owedPick <= 0);
      if (mode === 'engine' && owedPick <= 0) add.title = 'No perk pick is currently owed.';
      if (needsFollowup) {
        add.textContent = 'Needs a follow-up choice';
        card.appendChild(el('p', 'hint', 'Not written partially: this perk also requires choosing a skill or replacing a trait.'));
      }
      add.addEventListener('click', () =>
        apply(() => {
          perkPick = availability.perk;
          if (mode === 'engine') {
            if ((characterEditorPerksOwed(save) ?? 0) <= 0) {
              throw new Error('The engine does not owe this character a perk selection.');
            }
            if (K().perkNames[availability.perk] === 'TAG' || K().perkNames[availability.perk] === 'MUTATE') {
              throw new Error('Tag! and Mutate! need an interactive second choice and are Expert-only here.');
            }
            const outcome = editor.addPerkChecked(availability.perk);
            if (!outcome.available) {
              const failed = outcome.requirements.find((r) => r.kind !== 'not-selectable' && !r.met);
              throw new Error(
                failed
                  ? `That perk is out of reach: ${requirementText(failed)}.`
                  : 'The engine would not add that perk.'
              );
            }
            session?.engineOps.push({ kind: 'takePerk', perk: availability.perk });
          } else {
            editor.addPerk(availability.perk);
          }
        })
      );
      card.appendChild(add);
      grid.appendChild(card);
    }
    into.appendChild(grid);

    perkSearch.oninput = () => {
      perkQuery = perkSearch.value;
      render();
    };
  };

  const killsTab = (into: HTMLElement) => {
    if (!session) return;
    const { save, editor } = session;
    const fields = el('div', 'se-fields');
    for (let i = 0; i < K().killTypeNames.length; i++) {
      fields.appendChild(
        numberField({
          label: K().killTypeNames[i]!,
          value: save.kills[i]!,
          commit: mode === 'engine' ? undefined : (v) => editor.setKillCount(i, v),
          readOnly: mode === 'engine',
        })
      );
    }
    into.appendChild(fields);
  };

  /**
   * The Globals tab, rebuilt.
   *
   * Three complaints, three changes:
   *
   *   - **Names were truncated to uselessness.** `GVAR_TOWN_REP_MODOC_HOSTILE`
   *     in a nowrap ellipsised column is `GVAR_TOWN_REP_M…`, which
   *     identifies nothing. The rows are now two-line: the full name on its own
   *     line, monospace, wrapping, with the number and the value under it.
   *   - **The search lost the first keystrokes.** The input is a persistent node
   *     now (see `persistentInput`), so a render mid-typing no longer replaces
   *     it, and focus and caret are put back afterwards.
   *   - **Meanings were missing or would have been invented.** Only the verified
   *     table gets a meaning. Everything else gets its name, its number and its
   *     value, plus the `.gam` file's own comment when the file carries one —
   *     labelled as the file's comment, not as ours.
   */
  const globalsTab = (into: HTMLElement) => {
    if (!session) return;
    const { save, editor, gvarName, gvarSource } = session;

    gvarSearch.value = gvarQuery;
    into.appendChild(gvarSearch);

    const provenance = el('div', 'se-skills-head');
    provenance.appendChild(el('span', 'hint', gvarSource));
    const toggle = explainToggle('gvars:overview', GVAR_EXPLANATION);
    if (toggle) provenance.appendChild(toggle);
    into.appendChild(provenance);
    const overview = explainPanel('gvars:overview', GVAR_EXPLANATION);
    if (overview) into.appendChild(overview);

    const byIndex = new Map(session.install?.globals?.vars.map((v) => [v.index, v]) ?? []);

    // Filtering only replaces the results. Detaching the search box from the
    // DOM inside its own `input` event makes Chromium roll back the keystroke
    // after the handler returns, even when the exact same node is re-appended.
    // Keeping the input attached also preserves selection and IME state.
    const results = el('div', 'se-gvar-results');
    into.appendChild(results);

    const renderResults = () => {
      const q = gvarQuery.trim().toUpperCase();
      const matches: number[] = [];
      for (let i = 0; i < save.gvars.length; i++) {
        if (!q || String(i) === q || gvarName(i).toUpperCase().includes(q)) matches.push(i);
      }

      const count = el('p', 'hint', '');
      count.dataset.gvarMatches = String(matches.length);
      count.textContent =
        matches.length > GVAR_PAGE
          ? `${matches.length} match — showing the first ${GVAR_PAGE}. Narrow the filter.`
          : `${matches.length} of ${save.gvars.length} globals.`;

      const list = el('div', 'se-gvars');
      for (const i of matches.slice(0, GVAR_PAGE)) {
        const row = el('div', 'se-gvar');
        row.dataset.gvar = String(i);

        const name = el('div', 'se-gvar-name', gvarName(i));
        row.appendChild(name);

        const controls = el('div', 'se-gvar-controls');
        controls.appendChild(el('span', 'se-gvar-index', `#${i}`));
        const input = el('input');
        input.type = 'text';
        input.inputMode = 'numeric';
        input.value = String(save.gvars[i]!);
        input.disabled = mode === 'engine';
        input.addEventListener('change', () => {
          const next = Number(input.value);
          if (!Number.isFinite(next)) {
            toast(`GVAR ${i}: not a number`, 'bad');
            render();
            return;
          }
          apply(() => editor.setGvar(i, next));
        });
        controls.appendChild(input);
        row.appendChild(controls);

        // A meaning only where one has actually been established. The verified
        // table is deliberately two entries long; a confidently wrong quest-flag
        // description is worse than none, and this is the place that temptation
        // would land.
        const meaning = VERIFIED_GVAR_MEANINGS.get(i);
        if (meaning) {
          const note = el('div', 'se-gvar-meaning', meaning);
          note.dataset.verified = '1';
          row.appendChild(note);
        }

        // The `.gam` file's own trailing comment, labelled as the file's. It is
        // hand-maintained and wrong in places, which is exactly why it is
        // attributed rather than presented as fact.
        const declared = byIndex.get(i);
        if (declared && declared.commentIndex !== null && declared.commentIndex !== i) {
          row.appendChild(
            el(
              'div',
              'se-gvar-comment',
              `vault13.gam comments this line "(${declared.commentIndex})"; declaration order makes it ${i}, and the engine indexes by declaration order.`
            )
          );
        }
        if (declared) {
          row.appendChild(
            el('div', 'se-gvar-comment', `vault13.gam declares it starting at ${declared.value}.`)
          );
        }

        list.appendChild(row);
      }
      results.replaceChildren(count, list);
    };

    renderResults();

    gvarSearch.oninput = () => {
      gvarQuery = gvarSearch.value;
      renderResults();
    };
  };

  /**
   * Spending the engine, lazily and on purpose.
   *
   * The bridge is a one-way door: `load` leaves the engine holding a game it
   * cannot hand back, so the reactor has to restart afterwards and the mode
   * switch locks. That is a fair price for an edit the player asked for and a
   * bad one for *opening a tab*, which is what this used to cost — the panel
   * loaded the save the moment the Inventory tab was looked at, purely so the
   * party roster would be there if it was wanted.
   *
   * So nothing is claimed until the player does something that genuinely needs
   * the engine:
   *
   *   - **staging any inventory operation**, because that is an edit; and
   *   - **asking for the party roster**, because companion inventories only
   *     exist inside the engine. SAVE.DAT nests them as opaque objects the
   *     codec does not walk, and the owner list itself comes out of the
   *     snapshot — there is no read-only route to either.
   *
   * The player's own carried items need none of this: `session.inventory` is
   * parsed at load and `inventoryReadForOwner(0)` falls back to it, so the tab
   * opens on a real, honest, read-only list.
   *
   * Fire-and-forget: the snapshot lands on a later tick and re-renders. Focus
   * survives that because the only text boxes here are `persistentInput`s.
   */
  const canClaimEngine = (available: boolean, bridge: SaveEditBridge | null): boolean =>
    !!session
    && !!bridge?.capabilities.partyInventory
    && available
    && !session.engineInventory
    && !inventoryLoading
    && !inventoryLoadError;

  const claimEngine = (reason: string): void => {
    if (!session) return;
    const status = host.bridgeStatus();
    const bridge = session.loadedBridge ?? status.bridge;
    const available = bridge !== null && (session.loadedBridge !== null || liveness === 'live');
    if (!canClaimEngine(available, bridge) || !bridge) return;
    const target = session;
    inventoryLoading = true;
    target.engineClaimed = true;
    engineClaimReason = reason;
    host.markEngineSpent();
    void bridge.loadInventory(target.slot).then(({ outcome, snapshot }) => {
      inventoryLoading = false;
      if (session !== target) return;
      if (!outcome.ok || !snapshot) {
        inventoryLoadError = outcome.ok
          ? 'the engine returned no inventory snapshot'
          : outcome.message;
        toast('Couldn’t load inventory. Restart the reactor and try again.', 'bad', 7000);
        return render();
      }
      target.loadedBridge = bridge;
      target.engineInventory = snapshot;
      inventoryOwner = 0;
      inventoryPid = null;
      render();
    });
  };

  const inventoryTab = (pane: HTMLElement) => {
    if (!session) return;
    const status = host.bridgeStatus();
    const bridge = session.loadedBridge ?? status.bridge;

    // Ask the engine once, the first time the tab is looked at. Re-rendering on
    // the answer is why this is fire-and-forget rather than awaited.
    if (status.bridge && !session.loadedBridge && liveness === 'unknown') {
      liveness = 'checking';
      const bridge = status.bridge;
      void bridge.probe().then((alive) => {
        if (liveness !== 'checking') return;
        liveness = alive ? 'live' : 'unserviced';
        if (api.isOpen && tab === 'Inventory') render();
      });
    }

    const available = bridge !== null && (session.loadedBridge !== null || liveness === 'live');
    const unavailableReason = status.reason
      ?? 'The engine only provides inventory access from its main menu.';

    const editable = available && !inventoryLoading;
    const canExchange = editable && inventoryOwner === 0 && (bridge?.capabilities.exchange ?? false);
    const note = el('div', 'note');
    note.textContent = inventoryLoading
      ? 'Loading player and party inventories…'
      : liveness === 'checking' && status.bridge
        ? 'Checking inventory access…'
        : status.reason
          ? 'Inventory editing is unavailable in this engine session.'
          : 'Inventory unavailable. Return to the game’s main menu, then retry.';
    if (!available && !inventoryLoading && liveness !== 'checking') note.classList.add('bad');
    // A stable hook for "the answer is not in yet", so nothing — a test or a
    // player — mistakes "still asking" for "refused".
    if (!available && liveness === 'checking' && status.bridge) note.classList.add('se-probing');
    if (!available || inventoryLoading) {
      if (!inventoryLoading && liveness !== 'checking') {
        if (status.bridge && !status.reason) {
          const retry = el('button', 'tiny ghost', 'Retry');
          retry.type = 'button';
          retry.addEventListener('click', () => {
            liveness = 'unknown';
            render();
          });
          note.append(' ', retry);
        }
        const details = el('details', 'se-error-details');
        details.append(el('summary', undefined, 'Details'), el('p', undefined, unavailableReason));
        note.appendChild(details);
      }
      pane.appendChild(note);
    }

    if (inventoryLoadError) {
      const error = el('div', 'note bad');
      error.appendChild(el('span', undefined, 'Couldn’t load inventory. Restart the reactor and try again.'));
      const restart = el('button', 'tiny ghost', 'Restart reactor');
      restart.type = 'button';
      restart.addEventListener('click', () => host.restart());
      error.append(' ', restart);
      const details = el('details', 'se-error-details');
      details.append(el('summary', undefined, 'Details'), el('p', undefined, inventoryLoadError));
      error.appendChild(details);
      pane.appendChild(error);
    } else {
      // The picker is drawn whether or not the engine has been spent, because
      // "who receives this" is a real question either way — it is just that
      // before the claim there is exactly one answerable receiver, the player,
      // and the rest of the row is the offer to go and find the others.
      const ownerRow = el('div', 'se-owner-picker');
      ownerRow.appendChild(el('span', 'se-owner-label', 'Receiver'));
      const ownerButton = (index: number, label: string): HTMLButtonElement => {
        const button = el('button', `tiny${index === inventoryOwner ? ' primary' : ' ghost'}`, label);
        button.type = 'button';
        button.dataset.inventoryOwner = String(index);
        button.setAttribute('aria-pressed', String(index === inventoryOwner));
        button.addEventListener('click', () => {
          inventoryOwner = index;
          inventoryPid = null;
          exchangeSlot = null;
          render();
        });
        return button;
      };

      if (session.engineInventory) {
        for (const owner of session.engineInventory.owners) {
          ownerRow.appendChild(
            ownerButton(
              owner.index,
              owner.index === 0
                ? `${owner.name || 'Player'} · player`
                : owner.name || `Party member ${owner.index}`
            )
          );
        }
      } else {
        ownerRow.appendChild(ownerButton(0, `${summarize(session.save).name || 'Player'} · player`));
        // The roster does not exist outside the engine: SAVE.DAT nests a
        // companion's items as an opaque object the codec never walks, and the
        // owner list comes out of the snapshot. So this is a button that says
        // what it costs rather than a list that cannot be drawn.
        if (bridge?.capabilities.partyInventory) {
          const claim = el('button', 'tiny ghost se-owner-claim', 'Party members — loads the save into the engine');
          claim.type = 'button';
          claim.dataset.inventoryOwnerClaim = '1';
          claim.disabled = !editable;
          claim.title =
            'Companion inventories are only readable from inside the engine, so listing them loads this save '
            + 'into it. The reactor restarts when you are done, and the mode switch locks until then.';
          claim.addEventListener('click', () => {
            claimEngine('Loading the party roster has loaded the save into the engine.');
            render();
          });
          ownerRow.appendChild(claim);
        }
      }
      pane.appendChild(ownerRow);
    }

    // Said once, where the staging happens, and only while it is still true.
    if (!session.engineClaimed && !inventoryLoadError) {
      pane.appendChild(
        el(
          'p',
          'hint se-engine-claim-hint',
          'Nothing is loaded into the engine yet — this list is read from the save itself. '
            + 'The first staged change (or listing the party) loads the save into the engine, and the reactor '
            + 'restarts once the write finishes.'
        )
      );
    }

    /**
     * Queues, never calls — the engine hears about the operations themselves at
     * Write. Staging is nevertheless the moment the engine gets claimed: an
     * inventory edit has no other route to the file, and claiming here rather
     * than at Write means the queue is built against the engine's own reading
     * of the save instead of the codec's approximation of it.
     */
    const queueOp = (op: 'give' | 'remove', p: number, n: number) => {
      if (!session) return;
      if (!Number.isInteger(p) || p < 0) {
        toast('item pid: needs a whole number', 'bad');
        return;
      }
      if (!Number.isInteger(n) || n < 1) {
        toast('count: needs to be at least 1', 'bad');
        return;
      }
      session.queue.push({ op, owner: inventoryOwner, pid: p, count: n });
      if (op === 'give') giveCount = 1;
      else removeCount = 1;
      claimEngine('Staging an inventory change has loaded the save into the engine.');
      render();
    };

    const grid = el('div', 'se-barter');
    grid.append(catalogSide(editable, canExchange, queueOp), carriedSide(editable, canExchange, queueOp));
    pane.appendChild(grid);

  };

  const inventoryReadForOwner = (owner: number): InventoryRead | null => {
    if (!session?.engineInventory) return owner === 0 ? session?.inventory ?? null : null;
    const entries: InventoryEntry[] = session.engineInventory.entries
      .filter((entry) => entry.owner === owner)
      .map((entry) => ({
        quantity: entry.count,
        pid: entry.pid,
        flags: entry.flags,
        equipped: equippedSlot(entry.flags),
        contents: [],
      }));
    return { ok: true, entries };
  };

  // --------------------------------------------------- the item hover card

  /**
   * The Inventory tab's hover card: what the proto says about the item under the
   * pointer, in both panes.
   *
   * ## Why one element and not one per row
   *
   * The catalog is ~540 rows on a stock install and every one of them is rebuilt
   * on every `render()`. Attaching a card, or even a listener, per row would pay
   * for 540 items to answer a question about one. So there is a single card that
   * lives on the *overlay* — outside `#saveedit-body`, which `render()` empties —
   * a single delegated listener pair on the body, and a per-pid content cache
   * that is filled on first hover and never for an item nobody looked at.
   *
   * ## Pointer policy
   *
   * Mouse only. `pointerover` fires for touch too, and on a touchscreen it fires
   * on the tap that is *also* selecting the item — so a touch user would get a
   * card covering the list they just acted on, with no hover-out to dismiss it.
   * Touch users lose nothing: tapping a row selects it, and the selection panel
   * below already carries the item's details. Keyboard users get the card on
   * focus, which is the accessible path, and `aria-describedby` points the
   * focused button at it so a screen reader reads it rather than discovering it.
   */
  const itemTip = el('div', 'se-item-tip');
  itemTip.id = 'se-item-tip';
  itemTip.setAttribute('role', 'tooltip');
  // `popover` puts it in the top layer, so it cannot be clipped by the scrolling
  // list it is anchored to and needs no z-index against the overlay. `manual`
  // rather than `auto`: this is not light-dismiss furniture, it follows hover.
  itemTip.setAttribute('popover', 'manual');
  overlay.appendChild(itemTip);

  const canPopover = typeof itemTip.showPopover === 'function';
  const itemTipCache = new Map<number, HTMLElement>();
  let itemTipPid: number | null = null;
  let itemTipAnchor: HTMLElement | null = null;

  const itemTipContent = (item: CatalogItem): HTMLElement => {
    const cached = itemTipCache.get(item.pid);
    if (cached) return cached;

    const detail = itemDetail(item, {
      perkName: (id) => session?.install?.chargen.perks[id]?.name ?? null,
      perkDescription: (id) => perkEntry(id)?.description || null,
    });
    const root = el('div', 'se-tip-body');

    const head = el('div', 'se-tip-head');
    head.append(el('span', 'se-tip-name', detail.name), el('span', 'se-tip-kind', detail.kind));
    root.appendChild(head);

    // An install without `pro_item.msg`, or with no entry at `messageId + 1`,
    // simply has no description. Saying "no description" would be inventing a
    // fact about the mod; the card just shows the stats it does have.
    if (detail.description) root.appendChild(el('p', 'se-tip-desc', detail.description));

    for (const group of detail.groups) {
      if (!group.rows.length) continue;
      if (group.label) root.appendChild(el('h4', 'se-tip-group', group.label));
      const list = el('dl', 'se-tip-rows');
      for (const row of group.rows) {
        const value = el('dd', undefined, row.value);
        if (row.note) value.appendChild(el('span', 'se-tip-note', row.note));
        list.append(el('dt', undefined, row.label), value);
      }
      root.appendChild(list);
    }

    const engine = el('div', 'se-tip-engine');
    for (const line of detail.engine) engine.appendChild(el('div', undefined, line));
    root.appendChild(engine);

    itemTipCache.set(item.pid, root);
    return root;
  };

  const placeItemTip = (): void => {
    if (!itemTipAnchor?.isConnected) return hideItemTip();
    const gutter = 10;
    itemTip.style.maxHeight = `${Math.max(140, window.innerHeight - gutter * 2)}px`;
    const anchor = itemTipAnchor.getBoundingClientRect();
    const rect = itemTip.getBoundingClientRect();
    // Beside the row, not over it: the row stays readable and the pointer never
    // has to cross the card to reach the next item. Right first, left when the
    // right pane leaves no room, clamped when neither side does.
    const right = anchor.right + 10;
    const left = anchor.left - rect.width - 10;
    const x = right + rect.width <= window.innerWidth - gutter
      ? right
      : left >= gutter
        ? left
        : Math.max(gutter, window.innerWidth - rect.width - gutter);
    const y = Math.min(
      Math.max(anchor.top, gutter),
      Math.max(gutter, window.innerHeight - rect.height - gutter)
    );
    itemTip.style.left = `${x}px`;
    itemTip.style.top = `${y}px`;
  };

  function hideItemTip(): void {
    if (itemTipPid === null) return;
    itemTipPid = null;
    itemTipAnchor?.querySelector('button.se-item-pick')?.removeAttribute('aria-describedby');
    itemTipAnchor = null;
    if (canPopover && itemTip.matches(':popover-open')) itemTip.hidePopover();
    window.removeEventListener('resize', placeItemTip);
  }

  const showItemTip = (row: HTMLElement): void => {
    if (!canPopover) return;
    const pid = Number(row.dataset.pid);
    if (!Number.isFinite(pid)) return;
    // Only items this install actually has a proto for. A pid the save carries
    // and the catalog does not know is exactly the case where we have nothing
    // true to say, so nothing is said.
    const item = session?.install?.catalog.byPid.get(pid);
    if (!item) return hideItemTip();
    if (itemTipPid === pid && itemTipAnchor === row) return;

    hideItemTip();
    itemTip.replaceChildren(itemTipContent(item));
    itemTipPid = pid;
    itemTipAnchor = row;
    row.querySelector('button.se-item-pick')?.setAttribute('aria-describedby', itemTip.id);
    itemTip.showPopover();
    placeItemTip();
    window.addEventListener('resize', placeItemTip);
  };

  const rowUnder = (target: EventTarget | null): HTMLElement | null =>
    target instanceof Element ? target.closest<HTMLElement>('.se-item[data-pid]') : null;

  body.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const row = rowUnder(event.target);
    if (row) showItemTip(row);
    else hideItemTip();
  });
  body.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    // Moving between the art and the label of the same row is not a leave.
    if (rowUnder(event.relatedTarget) === itemTipAnchor && itemTipAnchor) return;
    hideItemTip();
  });
  body.addEventListener('focusin', (event) => {
    const row = rowUnder(event.target);
    if (row) showItemTip(row);
  });
  body.addEventListener('focusout', (event) => {
    if (rowUnder(event.target) === itemTipAnchor) hideItemTip();
  });
  // The lists scroll inside themselves, which moves the anchor without firing a
  // pointer event. Capture, because scroll does not bubble.
  body.addEventListener('scroll', () => hideItemTip(), true);
  overlay.addEventListener('keydown', (event) => {
    // Dismiss, but do not consume: Escape still closes the editor, which is what
    // a player pressing it twice expects and what it did before this card.
    if (event.key === 'Escape') hideItemTip();
  });

  // ------------------------------------------------------- the barter screen

  /**
   * One row of the two-pane picker: art, name, and the pid as secondary text.
   *
   * The pid stays visible on purpose. It is how the bridge is actually
   * addressed, it is what every wiki and every other editor talks in, and it is
   * the only thing that disambiguates two items a mod has given the same name.
   */
  const itemRow = (opts: {
    pid: number;
    name: string;
    kind: string;
    fid: number;
    selected: boolean;
    countText?: string;
    equippedText?: string;
    onSelect: () => void;
    onDoubleClick?: () => void;
    pendingText?: string;
    extra?: HTMLElement;
    /** A short tag beside the name, e.g. the wield warning. Dims the row. */
    warning?: { text: string; title: string };
  }): HTMLElement => {
    const row = el(
      'div',
      `se-item${opts.selected ? ' sel' : ''}${opts.pendingText ? ' pending' : ''}${opts.warning ? ' se-item-warned' : ''}`
    );
    row.dataset.pid = String(opts.pid);

    const pick = el('button', 'se-item-pick');
    pick.type = 'button';
    pick.setAttribute('aria-pressed', String(opts.selected));
    pick.appendChild(artTile(session?.install ?? null, opts.fid, opts.name));

    const text = el('div', 'se-item-text');
    const line = el('div', 'se-item-name');
    line.appendChild(el('span', 'se-item-label', opts.name));
    if (opts.countText) line.appendChild(el('span', 'se-item-count', opts.countText));
    if (opts.warning) {
      const tag = el('span', 'se-item-warn-tag', opts.warning.text);
      tag.title = opts.warning.title;
      tag.dataset.warnTag = '1';
      line.appendChild(tag);
    }
    text.appendChild(line);
    const sub = el('div', 'se-item-sub');
    sub.appendChild(el('span', 'se-item-pid', `#${opts.pid}`));
    sub.appendChild(el('span', undefined, opts.kind));
    text.appendChild(sub);
    if (opts.equippedText) text.appendChild(el('div', 'se-item-equipped', opts.equippedText));
    if (opts.pendingText) text.appendChild(el('div', 'se-item-pending-text', opts.pendingText));
    pick.appendChild(text);
    if (opts.onDoubleClick) pick.title = 'Double-click to stage one';
    let singleClickTimer: number | null = null;
    pick.addEventListener('click', () => {
      if (!opts.onDoubleClick) return opts.onSelect();
      if (singleClickTimer !== null) window.clearTimeout(singleClickTimer);
      singleClickTimer = window.setTimeout(() => {
        singleClickTimer = null;
        opts.onSelect();
      }, 220);
    });
    if (opts.onDoubleClick) pick.addEventListener('dblclick', (event) => {
      event.preventDefault();
      if (singleClickTimer !== null) window.clearTimeout(singleClickTimer);
      singleClickTimer = null;
      opts.onDoubleClick?.();
    });
    row.appendChild(pick);

    if (opts.extra) row.appendChild(opts.extra);
    return row;
  };

  /**
   * The inline quantity control the owner asked for: minus, an editable count,
   * plus, and the button that stages the delta.
   *
   * The count is a *delta against what is carried*, not a target — the engine
   * ops are "give N" and "remove N" and dressing them up as "set the total to N"
   * would hide the fact that a Remove can come up short when part of the stack
   * is equipped.
   */
  const quantityControl = (opts: {
    label: string;
    value: number;
    max: number | null;
    enabled: boolean;
    disabledNote: string;
    onCount: (n: number) => void;
    onStage: (n: number) => void;
    testId?: string;
  }): HTMLElement => {
    const wrap = el('div', 'se-qty');
    if (opts.testId) wrap.dataset.qty = opts.testId;

    const clamp = (n: number) => {
      const whole = Number.isFinite(n) ? Math.round(n) : 1;
      const lower = Math.max(1, whole);
      return opts.max !== null ? Math.min(lower, Math.max(1, opts.max)) : lower;
    };

    const minus = el('button', 'tiny ghost', '−');
    minus.type = 'button';
    minus.dataset.qtyStep = '-1';
    minus.disabled = !opts.enabled || opts.value <= 1;

    const input = el('input');
    input.type = 'text';
    input.inputMode = 'numeric';
    input.className = 'se-qty-count';
    input.min = '1';
    if (opts.max !== null) input.max = String(opts.max);
    input.value = String(opts.value);
    input.disabled = !opts.enabled;

    const plus = el('button', 'tiny ghost', '+');
    plus.type = 'button';
    plus.dataset.qtyStep = '1';
    plus.disabled = !opts.enabled || (opts.max !== null && opts.value >= opts.max);

    minus.addEventListener('click', () => {
      opts.onCount(clamp(opts.value - 1));
      render();
    });
    plus.addEventListener('click', () => {
      opts.onCount(clamp(opts.value + 1));
      render();
    });
    input.addEventListener('change', () => {
      opts.onCount(clamp(Number(input.value)));
      render();
    });

    wrap.append(minus, input, plus);
    if (opts.max !== null) wrap.appendChild(el('span', 'se-aside', `of ${opts.max}`));

    const stage = el('button', `tiny${opts.enabled ? ' primary' : ''}`, opts.label);
    stage.type = 'button';
    stage.disabled = !opts.enabled;
    stage.addEventListener('click', () => opts.onStage(clamp(Number(input.value))));
    wrap.appendChild(stage);

    if (!opts.enabled && opts.disabledNote) wrap.appendChild(el('p', 'hint', opts.disabledNote));
    return wrap;
  };

  const pendingBand = (kinds: Array<PendingItem['op']>, owner: number): HTMLElement | null => {
    if (!session) return null;
    const pending = session.queue
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => kinds.includes(entry.op) && (entry.op === 'exchange' ? owner === 0 : entry.owner === owner));
    if (!pending.length) return null;
    const band = el('div', 'se-pending-band');
    band.appendChild(el('div', 'se-pending-title', `Pending (${pending.length})`));
    for (const { entry, index } of pending) {
      const row = el('div', 'se-pending-row');
      const name = (pid: number) => session?.install?.catalog.byPid.get(pid)?.name ?? `item ${pid}`;
      row.appendChild(el(
        'span',
        undefined,
        entry.op === 'exchange'
          ? `Exchange ${name(entry.fromPid)} → ${name(entry.toPid)}`
          : `${entry.op === 'give' ? '+' : '−'}${entry.count} ${name(entry.pid)}`,
      ));
      const drop = el('button', 'tiny ghost', 'Undo');
      drop.type = 'button';
      drop.addEventListener('click', () => {
        session?.queue.splice(index, 1);
        render();
      });
      row.appendChild(drop);
      band.appendChild(row);
    }
    return band;
  };

  /** Left pane: everything the install *could* make, searchable. */
  /**
   * Who the wield question is being asked about, when it is a question at all.
   *
   * Three ways to get null, all of them "say nothing" rather than "say no":
   * the receiver is the player (every PC art base ships the whole weapon set —
   * checked against a stock install rather than assumed), this install's
   * archives carry no critter art (`critterArt.ok`), or the companion's proto
   * does not resolve to an art base. The base resolves lazily out of
   * `proto/critters/*.pro`, so the first render after picking a companion
   * schedules the read and the answer lands on the next one.
   */
  const wieldContext = (): { ownerName: string; base: string } | null => {
    if (inventoryOwner === 0) return null;
    const critterArt = session?.install?.critterArt;
    const owner = session?.engineInventory?.owners.find((candidate) => candidate.index === inventoryOwner);
    if (!critterArt?.ok || !owner) return null;
    const base = critterArt.resolvedArtBase(owner.pid);
    if (base === undefined) {
      void critterArt.artBase(owner.pid).then(() => {
        if (session?.install?.critterArt === critterArt) render();
      });
      return null;
    }
    if (base === null) return null;
    return { ownerName: owner.name || `party member ${owner.index}`, base };
  };

  /**
   * The engine's own answer to "can this critter hold this?" — see
   * `saveedit/wieldable.ts` for the derivation. Null for anything that is not a
   * weapon, because nothing else is ever put in a hand.
   */
  const wieldVerdict = (item: CatalogItem, base: string): boolean | null => {
    if (item.itemType !== 'weapon') return null;
    const critterArt = session?.install?.critterArt;
    if (!critterArt?.ok) return null;
    const path = wieldArtPath(base, item.fields['animationCode'] ?? 0, item.fields['extendedFlags'] ?? 0);
    return path !== null && critterArt.hasArt(path);
  };

  const catalogSide = (
    available: boolean,
    canExchange: boolean,
    queueOp: (op: 'give' | 'remove', pid: number, count: number) => void
  ): HTMLElement => {
    const side = el('div', 'se-side se-catalog');
    side.appendChild(section('Item catalog'));

    const catalog = session?.install?.catalog;
    if (!catalog || !catalog.ok) {
      side.appendChild(
        el(
          'p',
          'hint',
          session?.installState === 'loading'
            ? "Reading this install's item protos…"
            : 'proto/items/items.lst could not be read from this install, so there is no catalog to search. '
              + 'Inventory edits still work if you know the pid.'
        )
      );
      return side;
    }

    catalogSearch.value = catalogQuery;
    const kind = el('select', 'se-kind');
    for (const [value, text] of [['all', 'every type'] as const, ...ITEM_TYPES.map((t) => [t, t] as const)]) {
      const opt = el('option', undefined, text);
      opt.value = value;
      kind.appendChild(opt);
    }
    kind.value = catalogType;

    const controls = el('div', 'se-side-controls');
    controls.append(catalogSearch, kind);
    side.appendChild(controls);

    // A sort key that stopped applying when the type changed would silently
    // order by nothing; snap back to the one every type has.
    const sorts = sortsFor(catalogType);
    if (!sorts.some((sort) => sort.key === catalogSort)) {
      catalogSort = 'name';
      catalogSortDescending = false;
    }
    const activeSort = sorts.find((sort) => sort.key === catalogSort) ?? sorts[0]!;

    const sortRow = el('div', 'se-side-controls se-catalog-filters');
    const sortSelect = el('select', 'se-kind');
    sortSelect.dataset.catalogSort = '1';
    sortSelect.setAttribute('aria-label', 'Sort the catalog');
    for (const sort of sorts) {
      const opt = el('option', undefined, `sort: ${sort.label}`);
      opt.value = sort.key;
      sortSelect.appendChild(opt);
    }
    sortSelect.value = activeSort.key;
    sortSelect.addEventListener('change', () => {
      catalogSort = sortSelect.value as CatalogSortKey;
      catalogSortDescending = CATALOG_SORTS.find((s) => s.key === catalogSort)?.descending ?? false;
      catalogScrollTop = 0;
      render();
    });
    const direction = el('button', 'tiny ghost se-sort-dir', catalogSortDescending ? '↓' : '↑');
    direction.type = 'button';
    direction.dataset.catalogSortDir = catalogSortDescending ? 'desc' : 'asc';
    direction.title = catalogSortDescending ? 'Highest first' : 'Lowest first';
    direction.setAttribute('aria-label', direction.title);
    direction.addEventListener('click', () => {
      catalogSortDescending = !catalogSortDescending;
      catalogScrollTop = 0;
      render();
    });
    sortRow.append(sortSelect, direction);

    // Stat filters, only where the stat means something. A damage type is a
    // weapon's own field; a caliber is the one number that pairs a weapon with
    // a box of ammo, so it is offered on both and nowhere else. Anything more
    // than this is a query language, and a query language is a worse search
    // box than the search box.
    const dropdown = (
      label: string,
      hook: string,
      options: ReadonlyArray<readonly [number, string]>,
      current: number,
      onPick: (value: number) => void
    ): HTMLSelectElement => {
      const select = el('select', 'se-kind');
      select.dataset[hook] = '1';
      select.setAttribute('aria-label', label);
      const any = el('option', undefined, label);
      any.value = '-1';
      select.appendChild(any);
      for (const [value, text] of options) {
        const opt = el('option', undefined, text);
        opt.value = String(value);
        select.appendChild(opt);
      }
      select.value = String(current);
      // A value the current type does not offer (a caliber left over from the
      // weapon list) has no matching option, so the select falls back to blank.
      // Treat that as "any" rather than filtering to nothing.
      if (select.selectedIndex < 0) {
        select.value = '-1';
        onPick(-1);
      }
      select.addEventListener('change', () => {
        onPick(Number(select.value));
        catalogPid = null;
        catalogScrollTop = 0;
        render();
      });
      return select;
    };

    const typed = catalog.items.filter((item) => catalogType === 'all' || item.itemType === catalogType);

    if (catalogType === 'weapon') {
      const present = [...new Set(typed.map((item) => item.fields['damageType'] ?? 0))].sort((a, b) => a - b);
      sortRow.appendChild(
        dropdown(
          'any damage type',
          'catalogDamageType',
          present.map((id) => [id, DAMAGE_TYPES[id] ?? `damage ${id}`] as const),
          catalogDamageType,
          (value) => {
            catalogDamageType = value;
          }
        )
      );
    } else {
      catalogDamageType = -1;
    }

    // Armour's answer to the same question, one step over: the selector does not
    // filter the list, it picks which of the seven DT/DR columns the sort reads.
    // It only appears while one of those sorts is active, because a control that
    // changes nothing is worse than no control. Types no armour in this install
    // has a number for are left out — sorting by a column of zeroes is a tie.
    if (catalogType === 'armor' && (activeSort.key === 'damageResistance' || activeSort.key === 'damageThreshold')) {
      const present = DAMAGE_TYPES.map((_, i) => i).filter((i) =>
        typed.some((item) =>
          (item.fields[armorDamageField('damageThreshold', i)] ?? 0) !== 0 ||
          (item.fields[armorDamageField('damageResistance', i)] ?? 0) !== 0
        )
      );
      // An install whose armour protects against nothing still gets a selector
      // with one entry rather than an empty dropdown.
      const offered = present.length ? present : [0];
      if (!offered.includes(catalogArmorDamageType)) catalogArmorDamageType = offered[0]!;
      const select = el('select', 'se-kind');
      select.dataset.catalogArmorDamageType = '1';
      select.setAttribute('aria-label', `Which damage type the ${activeSort.label} sort reads`);
      select.title = `Sorting by ${activeSort.label} against ${DAMAGE_TYPES[catalogArmorDamageType]} damage.`;
      for (const i of offered) {
        const opt = el('option', undefined, `vs ${DAMAGE_TYPES[i]}`);
        opt.value = String(i);
        select.appendChild(opt);
      }
      select.value = String(catalogArmorDamageType);
      select.addEventListener('change', () => {
        catalogArmorDamageType = Number(select.value);
        catalogPid = null;
        catalogScrollTop = 0;
        render();
      });
      sortRow.appendChild(select);
    }

    if (catalogType === 'weapon' || catalogType === 'ammo') {
      const present = [...new Set(typed.map((item) => item.fields['caliber'] ?? 0))].sort((a, b) => a - b);
      sortRow.appendChild(
        dropdown(
          'any caliber',
          'catalogCaliber',
          present.map((id) => [id, CALIBER_TYPES[id] ?? `caliber ${id}`] as const),
          catalogCaliber,
          (value) => {
            catalogCaliber = value;
          }
        )
      );
    } else {
      catalogCaliber = -1;
    }

    // The wield filter is only a question when a companion is the receiver, and
    // only answerable when this install's archives carry the critter art.
    const wield = wieldContext();
    if (!wield) catalogWieldableOnly = false;
    if (wield) {
      const toggle = el(
        'button',
        `tiny${catalogWieldableOnly ? ' primary' : ' ghost'}`,
        `wieldable by ${wield.ownerName}`
      );
      toggle.type = 'button';
      toggle.dataset.catalogWieldable = catalogWieldableOnly ? 'on' : 'off';
      toggle.setAttribute('aria-pressed', String(catalogWieldableOnly));
      toggle.title =
        `${wield.ownerName}'s art (${wield.base}) is only drawn holding some weapons. `
        + 'Giving one they cannot hold is still allowed — they carry it, they just cannot equip it.';
      toggle.addEventListener('click', () => {
        catalogWieldableOnly = !catalogWieldableOnly;
        catalogPid = null;
        catalogScrollTop = 0;
        render();
      });
      sortRow.appendChild(toggle);
    }

    side.appendChild(sortRow);

    const q = catalogQuery.trim().toLowerCase();
    const matches = typed.filter(
      (item) =>
        (!q || item.name.toLowerCase().includes(q) || String(item.pid) === q) &&
        (catalogDamageType < 0 || (item.fields['damageType'] ?? 0) === catalogDamageType) &&
        (catalogCaliber < 0 || (item.fields['caliber'] ?? 0) === catalogCaliber) &&
        (!catalogWieldableOnly || !wield || wieldVerdict(item, wield.base) !== false)
    );

    // Sorted on a copy: `catalog.items` is the install, not a view of it.
    const byName = (a: CatalogItem, b: CatalogItem) => a.name.localeCompare(b.name) || a.pid - b.pid;
    const sortContext: CatalogSortContext = { armorDamageType: catalogArmorDamageType };
    matches.sort((a, b) => {
      if (!activeSort.value) return catalogSortDescending ? byName(b, a) : byName(a, b);
      const delta = activeSort.value(a, sortContext) - activeSort.value(b, sortContext);
      // Ties break by pid where the tie is a family — "everything .44, in pid
      // order", "every armour with DT 4 against fire". A family reads better in
      // file order than alphabetically. Everything else breaks by name.
      const tie = activeSort.tiePid ? a.pid - b.pid : byName(a, b);
      return (catalogSortDescending ? -delta : delta) || tie;
    });

    const count = el('p', 'hint', '');
    count.textContent = `${matches.length} of ${catalog.items.length} items.`;

    const list = el('div', 'se-items');
    list.dataset.catalogList = '1';
    list.addEventListener('scroll', () => {
      catalogScrollTop = list.scrollTop;
    });
    for (const item of matches) {
      const wieldable = wield ? wieldVerdict(item, wield.base) : null;
      list.appendChild(
        itemRow({
          pid: item.pid,
          name: item.name,
          kind: item.itemType,
          fid: item.inventoryFid,
          selected: catalogPid === item.pid,
          warning:
            wieldable === false && wield
              ? {
                  text: "can't wield",
                  title:
                    `${wield.ownerName} has no ${WEAPON_ANIMATION_LABELS[item.fields['animationCode'] ?? 0] ?? 'weapon'} `
                    + `animation — this install ships no ${wield.base} art holding it, so the engine refuses the equip. `
                    + 'Giving it is still fine: it goes in their pack.',
                }
              : undefined,
          onSelect: () => {
            catalogPid = item.pid;
            giveCount = Math.max(1, giveCount);
            render();
          },
          onDoubleClick: available ? () => queueOp('give', item.pid, 1) : undefined,
        })
      );
    }

    const selected: CatalogItem | null =
      catalogPid === null ? null : (catalog.byPid.get(catalogPid) ?? null);

    const stage = el('div');
    // When an Exchange is being staged, the left pane's job changes: it is
    // picking the replacement, not handing over a gift. Saying so is cheaper
    // than a mode indicator somewhere else on the screen.
    if (canExchange && exchangeSlot !== null) {
      const equipped = equippedStackFor(exchangeSlot);
      const note = el('div', 'note se-exchange-note');
      note.dataset.exchangeSlot = String(exchangeSlot);
      note.textContent = selected
        ? `Exchange the ${EQUIP_SLOT_NAMES[exchangeSlot]} for ${selected.name}. The engine takes the old one off, removes it, gives you this, and puts it on — in that order, in one batch.`
        : `Pick the item to put in the ${EQUIP_SLOT_NAMES[exchangeSlot]}.`;
      stage.appendChild(note);

      const row = el('div', 'se-stage');
      const go = el('button', 'tiny primary', 'Stage exchange');
      go.type = 'button';
      go.dataset.exchangeStage = '1';
      go.disabled = !selected || !equipped;
      go.addEventListener('click', () => {
        if (!session || !selected || !equipped || exchangeSlot === null) return;
        session.queue.push({
          op: 'exchange',
          equipSlot: exchangeSlot,
          fromPid: equipped.pid,
          toPid: selected.pid,
        });
        exchangeSlot = null;
        // Same rule as `queueOp`: an exchange is an inventory edit, so it is one
        // of the things that spends the engine. It does not route through
        // `queueOp` because it stages four ops, not one.
        claimEngine('Staging an inventory change has loaded the save into the engine.');
        render();
      });
      const cancel = el('button', 'tiny ghost', 'Cancel');
      cancel.type = 'button';
      cancel.addEventListener('click', () => {
        exchangeSlot = null;
        render();
      });
      row.append(go, cancel);
      stage.appendChild(row);
    } else {
      stage.appendChild(
        quantityControl({
          label: selected ? `Give ${selected.name}` : 'Give',
          value: giveCount,
          max: null,
          enabled: available && selected !== null,
          disabledNote: '',
          testId: 'give',
          onCount: (n) => {
            giveCount = n;
          },
          onStage: (n) => {
            if (selected) queueOp('give', selected.pid, n);
          },
        })
      );
    }

    catalogSearch.oninput = () => {
      catalogQuery = catalogSearch.value;
      catalogPid = null;
      catalogScrollTop = 0;
      render();
    };
    kind.addEventListener('change', () => {
      catalogType = kind.value as 'all' | ItemTypeName;
      catalogPid = null;
      catalogScrollTop = 0;
      render();
    });

    side.append(count, list);
    // Selection and staging re-render the panel. Restore the scroll position
    // after the replacement list is attached so clicking a deep catalog item
    // does not throw the player back to the first pid.
    list.scrollTop = catalogScrollTop;
    requestAnimationFrame(() => {
      if (list.isConnected) list.scrollTop = catalogScrollTop;
    });
    side.appendChild(stage);
    return side;
  };

  /** The equipped stack occupying a given slot, if the inventory could be read. */
  const equippedStackFor = (slot: EquipSlot): InventoryStack | null => {
    const read = inventoryReadForOwner(0);
    if (!read || !read.ok) return null;
    const wanted = EQUIP_SLOT_NAMES[slot];
    return (
      stackInventory(read.entries).find((s) => s.equippedCount > 0 && s.slots.some((n) => matchesSlot(n, wanted)))
      ?? null
    );
  };

  /** Right pane: what the save actually holds. */
  const carriedSide = (
    available: boolean,
    canExchange: boolean,
    queueOp: (op: 'give' | 'remove', pid: number, count: number) => void
  ): HTMLElement => {
    const side = el('div', 'se-side se-carried');
    const receiver = session?.engineInventory?.owners.find((owner) => owner.index === inventoryOwner);
    side.appendChild(section(receiver ? `${receiver.name || 'Party member'} receives` : 'Receiver inventory'));

    const read = inventoryReadForOwner(inventoryOwner);
    if (!read) {
      // Three different "no list yet", and only one of them is a problem. The
      // protos may still be loading; a party member other than the dude is only
      // ever listed from the engine's snapshot, and having none is ordinary;
      // and the protos may genuinely have failed to read, which is the one
      // worth saying out loud.
      const catalogFailed = session?.install ? !session.install.catalog.ok : false;
      side.appendChild(
        el(
          'p',
          'hint',
          session?.installState === 'loading' || !session?.install
            ? "Reading this install's item protos…"
            : catalogFailed
              ? "This install's item protos could not be read, so the carried items cannot be listed. "
                + 'The save itself is untouched — this pane is display only.'
              : 'A party member’s carried items are listed from the running engine, which has not handed over a '
                + 'snapshot yet. Stage a change, or pick this character in the reactor, to fill this in.'
        )
      );
      return side;
    }
    if (!read.ok) {
      const note = el('div', 'note bad');
      note.textContent =
        `The carried items could not be listed: ${read.reason}. That usually means the protos on this install `
        + 'are not the ones the save was written with. Nothing is wrong with the save, and the rest of the editor is unaffected — '
        + 'this pane is display only and the codec never writes this part of the file.';
      side.appendChild(note);
      return side;
    }

    const stacks = stackInventory(read.entries).sort((a, b) => b.count - a.count);
    const byPid = session?.install?.catalog.byPid;

    const removable = (stack: InventoryStack) => Math.max(0, stack.count - stack.equippedCount);

    const list = el('div', 'se-items');
    const pending = pendingBand(['give', 'remove', 'exchange'], inventoryOwner);
    if (pending) list.appendChild(pending);
    if (!stacks.length) {
      if (pending) side.appendChild(list);
      side.appendChild(el('p', 'hint', `${inventoryOwner === 0 ? 'This character' : 'This party member'} is carrying nothing yet.`));
      return side;
    }
    for (const stack of stacks) {
      const item = byPid?.get(stack.pid);
      const equipped = stack.equippedCount
        ? `equipped (${stack.slots.join(', ')})`
        : stack.inContainerOnly
          ? 'inside a container'
          : '';
      const pendingRemove = session?.queue.reduce(
        (total, entry) => total + (entry.op === 'remove' && entry.owner === inventoryOwner && entry.pid === stack.pid ? entry.count : 0),
        0,
      ) ?? 0;
      const pendingGive = session?.queue.reduce(
        (total, entry) => total + (entry.op === 'give' && entry.owner === inventoryOwner && entry.pid === stack.pid ? entry.count : 0),
        0,
      ) ?? 0;
      const pendingDelta = pendingGive - pendingRemove;

      // The exchange affordance lives on the row it applies to, because that is
      // where the question "can I swap this?" is actually asked.
      let extra: HTMLElement | undefined;
      if (stack.equippedCount > 0) {
        extra = el('div', 'se-item-extra');
        if (canExchange) {
          for (const slotName of stack.slots) {
            const slot = slotFromName(slotName);
            if (slot === null) continue;
            const button = el('button', 'tiny ghost', `Exchange ${slotName}`);
            button.type = 'button';
            button.dataset.exchangeStart = String(slot);
            button.addEventListener('click', () => {
              exchangeSlot = slot;
              catalogPid = null;
              render();
            });
            extra.appendChild(button);
          }
        } else {
          extra.appendChild(
            el(
              'p',
              'hint',
              'Equipped items cannot be removed from here. Unequip it in game, save, and come back — '
              + 'this engine build has no wield operation for the shell to use.'
            )
          );
        }
      }

      list.appendChild(
        itemRow({
          pid: stack.pid,
          name: item?.name ?? `item ${stack.pid}`,
          kind: item?.itemType ?? 'unknown to this install',
          fid: item?.inventoryFid ?? 0,
          countText: `×${stack.count}`,
          pendingText: pendingDelta
            ? `pending ${pendingDelta > 0 ? '+' : '−'}${Math.abs(pendingDelta)} · projected ×${Math.max(0, stack.count + pendingDelta)}`
            : undefined,
          equippedText: equipped,
          selected: inventoryPid === stack.pid,
          onSelect: () => {
            inventoryPid = stack.pid;
            removeCount = Math.max(1, Math.min(removeCount, removable(stack) || 1));
            render();
          },
          onDoubleClick: available && removable(stack) > pendingRemove
            ? () => queueOp('remove', stack.pid, 1)
            : undefined,
          extra,
        })
      );
    }

    const stack = stacks.find((s) => s.pid === inventoryPid) ?? null;
    const item = stack ? byPid?.get(stack.pid) : null;
    const alreadyPending = stack
      ? session?.queue.reduce(
          (total, entry) => total + (entry.op === 'remove' && entry.owner === inventoryOwner && entry.pid === stack.pid ? entry.count : 0),
          0,
        ) ?? 0
      : 0;
    const max = stack ? Math.max(0, removable(stack) - alreadyPending) : 0;
    const stage = quantityControl({
      label: stack ? `Remove ${item?.name ?? `item ${stack.pid}`}` : 'Remove',
      value: Math.min(Math.max(1, removeCount), Math.max(1, max)),
      max: stack ? max : null,
      enabled: available && stack !== null && max > 0,
      disabledNote: !available
        ? ''
        : !stack
          ? ''
          : canExchange
            ? 'Every one of these is equipped. Use Exchange on the row to swap it.'
            : 'Every one of these is equipped. Unequip it in game, save, and come back.',
      testId: 'remove',
      onCount: (n) => {
        removeCount = n;
      },
      onStage: (n) => {
        if (stack) queueOp('remove', stack.pid, Math.min(Math.max(1, n), max));
      },
    });

    side.append(list, stage);
    return side;
  };

  // ------------------------------------------------------------------ render

  const summaryInto = (into: HTMLElement) => {
    if (!session) return;
    const s = summarize(session.save);
    const head = el('div', 'se-summary');
    head.appendChild(el('div', 'who', s.name || '(unnamed)'));
    head.appendChild(
      el(
        'div',
        'desc',
        `${s.description || '(no description)'} · level ${s.level} · ${s.experience} XP · ${s.hitPoints}/${s.maximumHitPoints} HP`
      )
    );
    into.appendChild(head);
  };

  const changesInto = (into: HTMLElement) => {
    if (!session) return;
    const { editor, slot, queue, engineOps, save } = session;
    const pending = pendingChangeCount();
    const hasPending = editor.changes.length > 0 || queue.length > 0 || engineOps.length > 0;
    const box = el('div', 'se-changes');
    const summary = el(
      'span',
      hasPending ? 'se-change-count pending' : 'se-change-count',
      hasPending ? `${pending} change${pending === 1 ? '' : 's'} pending` : 'No pending changes'
    );

    if (hasPending) {
      const details = el('details', 'se-change-details');
      const disclosure = el('summary', undefined, 'Review changes');
      details.appendChild(disclosure);
      const list = el('table', 'kv');
      for (const c of editor.changes) {
        const tr = el('tr');
        tr.append(el('td', undefined, c.what), el('td', undefined, `${c.from} → ${c.to}`));
        list.appendChild(tr);
      }
      // Queued items are in the same list because they are the same promise —
      // "this is what Write will do" — even though they take a different route
      // to the file. The label says which route.
      queue.forEach((entry, index) => {
        const tr = el('tr', 'se-queued');
        const name = (pid: number) => session?.install?.catalog.byPid.get(pid)?.name ?? `item ${pid}`;
        const ownerName = (owner: number) => {
          if (owner === 0) return 'player';
          return session?.engineInventory?.owners.find((candidate) => candidate.index === owner)?.name
            || `party member ${owner}`;
        };
        if (entry.op === 'exchange') {
          tr.append(
            el('td', undefined, `inventory (engine): exchange ${EQUIP_SLOT_NAMES[entry.equipSlot]}`),
            el('td', undefined, `${name(entry.fromPid)} → ${name(entry.toPid)}`)
          );
        } else {
          const named = session?.install?.catalog.byPid.get(entry.pid)?.name;
          tr.append(
            el('td', undefined, `inventory (${ownerName(entry.owner)}, engine): ${entry.op}${named ? ` ${named}` : ''}`),
            // The pid form is what the bridge is actually given, so it is what
            // the change list promises.
            el('td', undefined, `${entry.count} × pid ${entry.pid}`)
          );
        }
        const drop = el('td');
        const btn = el('button', 'tiny ghost', 'Undo');
        btn.addEventListener('click', () => {
          session?.queue.splice(index, 1);
          render();
        });
        drop.appendChild(btn);
        tr.appendChild(drop);
        list.appendChild(tr);
      });
      details.appendChild(list);
      box.append(summary, details);
    } else {
      box.appendChild(summary);
    }

    const row = el('div', 'se-change-actions');
    const discard = el('button', 'ghost', 'Discard');
    discard.disabled = !hasPending;
    discard.addEventListener('click', async () => {
      if (!session || !(await confirmDanger(`Discard ${pending} pending change${pending === 1 ? '' : 's'}?`))) return;
      const currentSlot = session.slot;
      load(currentSlot);
      render();
    });
    row.appendChild(discard);

    const supported = headerVersionIsSupported(save.header, save.game);
    const write = el('button', 'primary', supported ? 'Save changes' : 'Save repaired copy');
    write.title = supported ? `Save changes to slot ${slot}` : 'Save a repaired copy';
    write.disabled = inventoryLoading
      || !hasPending;
    // Belt and braces on top of the per-call trap handling inside writeBack: a
    // rejected promise from a click handler is invisible, and a Write that
    // silently does nothing is the worst outcome this panel has.
    write.addEventListener('click', () => {
      void writeBack().catch((e) => toast(`Write failed: ${(e as Error).message}`, 'bad', 9000));
    });
    row.appendChild(write);
    box.appendChild(row);
    into.appendChild(box);
  };

  const failureInto = (into: HTMLElement, f: Failure) => {
    const note = el('div', f.kind === 'unsupported-game' ? 'note' : 'note bad');
    note.textContent =
      f.kind === 'unsupported-game'
        ? f.message
        : `Slot ${f.slot} could not be parsed: ${f.message}` +
          (f.offset === undefined ? '.' : ` (at byte ${f.offset}).`) +
          ' The editor will not offer a partial edit — everything after an unread region is guesswork.';
    into.appendChild(note);
    const row = el('div', 'row');
    const exportBtn = el('button', 'ghost', `Export slot ${f.slot} instead`);
    exportBtn.addEventListener('click', async () => {
      const FS = getFS();
      if (!FS) return;
      try {
        const { blob, filename } = await exportSlot(FS, f.slot);
        downloadBlob(blob, filename);
      } catch (e) {
        toast(`Export failed: ${(e as Error).message}`, 'bad');
      }
    });
    row.appendChild(exportBtn);
    into.appendChild(row);
  };

  /**
   * Hover linkage, wired once on the pane rather than per field.
   *
   * Both directions come free: `linkedFields` is an undirected graph, so
   * hovering CARRY_WEIGHT lights STRENGTH exactly as hovering STRENGTH lights
   * CARRY_WEIGHT. Keyboard focus does the same thing as the mouse, because a
   * hover-only affordance is invisible to anyone tabbing through the form.
   */
  const wireLinkage = (pane: HTMLElement) => {
    const clear = () => {
      for (const node of pane.querySelectorAll('.se-linked, .se-link-source')) {
        node.classList.remove('se-linked', 'se-link-source');
      }
    };
    const light = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return;
      const field = target.closest<HTMLElement>('.se-field[data-field]');
      if (!field) return;
      clear();
      const key = field.dataset.field!;
      field.classList.add('se-link-source');
      for (const linked of linkedFields(key)) {
        for (const node of pane.querySelectorAll(`.se-field[data-field="${cssEscape(linked)}"]`)) {
          node.classList.add('se-linked');
        }
      }
    };
    pane.addEventListener('mouseover', (e) => light(e.target));
    pane.addEventListener('focusin', (e) => light(e.target));
    pane.addEventListener('mouseleave', clear);
  };

  function render(): void {
    const panelScrollTop = body.scrollTop;
    captureFocus();
    // The card is anchored to a row that is about to be detached, and a detached
    // anchor gets no pointerout. Drop it here rather than leave it floating over
    // the rebuilt list.
    hideItemTip();
    body.replaceChildren();
    actionBar.replaceChildren();
    title.textContent = session ? 'Edit save' : failure ? `Slot ${failure.slot}` : 'Edit save';
    // Whether the player's install has been read yet, on the panel rather than
    // buried in one tab's copy: two tabs depend on it, and a test needs one
    // place to wait on.
    body.dataset.install = session?.installState ?? 'none';
    body.dataset.mode = mode;
    if (restarting) {
      // Nothing else is offered: the engine behind this panel is spent, and
      // every control on it would be acting on an engine that cannot answer.
      const note = el('div', 'note');
      note.textContent =
        `Slot ${session?.slot} written. Rules-checked edits run inside the engine and leave it without a game loop, `
        + 'so the reactor is restarting now. Your saves are already on disk.';
      body.appendChild(note);
      restorePanelScroll(panelScrollTop);
      return;
    }
    if (failure) {
      failureInto(body, failure);
      restorePanelScroll(panelScrollTop);
      return;
    }
    if (!session) {
      body.appendChild(el('p', 'hint', 'No slot open.'));
      restorePanelScroll(panelScrollTop);
      return;
    }

    summaryInto(body);
    body.appendChild(modeStrip());

    const strip = el('div', 'se-tabs');
    strip.setAttribute('role', 'tablist');
    strip.setAttribute('aria-label', 'Save editor sections');
    const tabButtons: HTMLButtonElement[] = [];
    for (const name of TABS) {
      const btn = el('button', `tiny${name === tab ? ' primary' : ' ghost'}`, name);
      const tabId = `saveedit-tab-${name.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}`;
      btn.id = tabId;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', String(name === tab));
      btn.setAttribute('aria-controls', 'saveedit-tabpanel');
      btn.tabIndex = name === tab ? 0 : -1;
      btn.addEventListener('click', () => {
        tab = name;
        focusTabAfterRender = true;
        body.scrollTop = 0;
        render();
      });
      strip.appendChild(btn);
      tabButtons.push(btn);
    }
    strip.addEventListener('keydown', (event) => {
      if (!(event.target instanceof HTMLButtonElement)) return;
      const current = tabButtons.indexOf(event.target);
      if (current < 0) return;
      let next = current;
      if (event.key === 'ArrowRight') next = (current + 1) % tabButtons.length;
      else if (event.key === 'ArrowLeft') next = (current - 1 + tabButtons.length) % tabButtons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabButtons.length - 1;
      else return;
      event.preventDefault();
      tabButtons[next]!.focus();
      tabButtons[next]!.click();
    });
    body.appendChild(strip);

    const pane = el('div', 'se-pane');
    pane.id = 'saveedit-tabpanel';
    pane.setAttribute('role', 'tabpanel');
    pane.setAttribute('aria-labelledby', tabButtons[TABS.indexOf(tab)]!.id);
    if (tab === 'Character') characterTab(pane);
    else if (tab === 'Skills') skillsTab(pane);
    else if (tab === 'Perks & Traits') perksTab(pane);
    else if (tab === 'Kills') killsTab(pane);
    else if (tab === 'Globals') globalsTab(pane);
    else inventoryTab(pane);
    wireLinkage(pane);
    body.appendChild(pane);

    changesInto(actionBar);
    restoreFocus();
    if (focusTabAfterRender) {
      body.querySelector<HTMLButtonElement>('.se-tabs [role="tab"][aria-selected="true"]')
        ?.focus({ preventScroll: true });
      focusTabAfterRender = false;
    }
    restorePanelScroll(panelScrollTop);
  }

  // ------------------------------------------------------------- load, write

  function load(slot: number): void {
    session = null;
    failure = null;
    gvarQuery = '';
    catalogQuery = '';
    catalogType = 'all';
    catalogSort = 'name';
    catalogSortDescending = false;
    catalogDamageType = -1;
    catalogArmorDamageType = 0;
    catalogCaliber = -1;
    catalogWieldableOnly = false;
    catalogScrollTop = 0;
    catalogPid = null;
    // Built from the install's protos and the install's perk names, either of
    // which a different slot may resolve differently. Cheaper to rebuild on
    // first hover than to reason about when it is still true.
    itemTipCache.clear();
    inventoryPid = null;
    inventoryOwner = 0;
    inventoryLoading = false;
    inventoryLoadError = null;
    engineClaimReason = null;
    perkQuery = '';
    perkPick = null;
    exchangeSlot = null;
    giveCount = 1;
    removeCount = 1;
    grantSkillPointsCount = 1;
    const FS = getFS();
    if (!FS) {
      failure = { slot, message: 'the engine has not booted, so the save filesystem is not mounted', offset: undefined };
      return;
    }
    let raw: Uint8Array;
    try {
      raw = FS.readFile(`${slotPath(slot)}/SAVE.DAT`, { encoding: 'binary' }) as Uint8Array;
    } catch {
      failure = { slot, message: 'no SAVE.DAT in the slot', offset: undefined };
      return;
    }
    let save: SaveDat;
    try {
      save = parseSaveDat(raw);
    } catch (e) {
      failure = {
        slot,
        message: e instanceof Error ? e.message : String(e),
        offset: e instanceof SaveParseError ? e.offset : undefined,
      };
      return;
    }
    // Engine-rules mode drives the engine's own serializer over the debug
    // bridge, which both engine builds carry. Without a live bridge — or on a
    // header version the codec does not vouch for — the slot opens in expert
    // mode, where every file-level edit still applies.
    if (!headerVersionIsSupported(save.header, save.game)
      || !host.bridgeStatus().bridge) {
      mode = 'expert';
    }
    // The install — names, protos, art — is an archive read and cannot be
    // synchronous. The session opens on the verified-names fallback and is
    // upgraded in place when the read lands, so the panel is usable
    // immediately and correct a moment later.
    const opened: Session = {
      slot,
      save,
      editor: new SaveEditor(save),
      queue: [],
      engineOps: [],
      loadedBridge: null,
      engineInventory: null,
      engineClaimed: false,
      gvarName: makeGvarNamer(undefined, save.game),
      gvarSource: LOADING_NAMES,
      install: null,
      installState: 'loading',
      inventory: null,
    };
    session = opened;
    void installData(FS).then((data) => {
      // A different slot (or none) is open by now: the answer is still cached
      // for whoever opens next, but it is not this session's.
      if (session !== opened) return;
      applyInstall(opened, data);
      render();
    });
  }

  /** Folds a resolved install into an open session. */
  function applyInstall(target: Session, data: InstallData): void {
    target.install = data;
    target.installState = data.globals || data.catalog.ok || data.chargen.ok ? 'ready' : 'unavailable';
    target.gvarName = makeGvarNamer(data.globals ?? undefined, target.save.game);
    target.gvarSource = data.globals
      ? `Names from ${data.globalsSource ?? 'this install'}: data/vault13.gam declares ${data.globals.vars.length}.`
        + (data.globals.commentDisagreements.length
          ? ` Its "// (N)" comments disagree with declaration order on ${data.globals.commentDisagreements.length} lines; declaration order is what the engine indexes by, and what is used here.`
          : '')
        + (data.globals.vars.length < target.save.gvars.length
          ? ` This save carries ${target.save.gvars.length}, so anything past #${data.globals.vars.length - 1} stays numeric.`
          : '')
      : 'data/vault13.gam is not in this install — not loose, and not in any mounted archive — so only the handful of '
        + 'verified names are shown; the rest stay numeric.';
    // The walk is the same on both games: `obj_write_obj` (fallout1-ce
    // object.cc:782) and `objectWrite` (engine/src/object.cc:737) emit the same
    // 18 int32s, and `proto_write_protoUpdateData` (fallout1-ce proto.cc:524)
    // and `objectDataWrite` (engine/src/proto.cc:662) the same prefix and the
    // same per-item-type tail. So all it needs is the install's own protos,
    // which the catalog now reads on Fallout 1 too. The engine bridge's
    // snapshot still wins when one has been claimed — `inventoryReadForOwner`
    // only falls back to this walk otherwise, and it is the only source for
    // party members.
    target.inventory = data.catalog.ok
      ? readInventory(target.save, (pid) => data.catalog.byPid.get(pid)?.itemType ?? null)
      : null;
  }

  /**
   * The whole write, both mechanisms, one backup, one round trip.
   *
   * Ordering is load-bearing and is spelled out in the module comment: back up,
   * run the bridge, *then* fold the scalar edits into what the engine wrote.
   * Any bridge failure rolls the slot back out of the backup that was taken a
   * moment ago — which is the same copy the player keeps, because taking two
   * would mean two "which one is the real original" answers.
   */
  async function writeBack(): Promise<void> {
    return queueSaveMutation(writeBackExclusive);
  }

  async function writeBackExclusive(): Promise<void> {
    const FS = getFS();
    if (!FS || !session) return;
    if (inventoryLoading) {
      toast('Wait for the party inventories to finish loading before writing.', 'warn');
      return;
    }
    const { slot, save, editor, queue, engineOps } = session;
    const engineWasClaimed = session.engineClaimed;
    if (!editor.changes.length && !queue.length && !engineOps.length) return;
    const needsEngine = queue.length > 0 || engineOps.length > 0;
    const supported = headerVersionIsSupported(save.header, save.game);
    if (!supported && needsEngine) {
      toast('An unsupported save can only be repaired as a scalar copy; engine operations are unavailable.', 'bad');
      return;
    }
    const repairTarget = supported
      ? null
      : listSlots(FS).find((candidate) => !candidate.occupied && candidate.role === 'manual')?.slot ?? null;
    if (!supported && repairTarget === null) {
      toast('No empty manual slot is available for the repaired copy. Free or export one first.', 'bad', 9000);
      return;
    }
    const writeSlot = repairTarget ?? slot;

    const status = host.bridgeStatus();
    const activeBridge = session.loadedBridge ?? status.bridge;
    if (needsEngine && !activeBridge) {
      toast(`Engine-rules changes cannot be written: ${status.reason}`, 'bad');
      return;
    }

    // Ask the engine whether anyone is actually reading the mailbox before
    // committing to anything. Only the main-menu loop services batches, and the
    // engine is not always in it — the opening movies alone are minutes of a
    // first-ever boot. Without this the player's first hint would be a two-minute
    // hang and a rollback; with it, nothing has been touched at all.
    if (needsEngine && !session.loadedBridge && activeBridge && !(await activeBridge.probe())) {
      toast(
        'The engine is not ready for rules-checked edits yet — it only takes them from the main menu. '
          + 'If the intro is still playing, skip it, or restart the reactor and try again. Nothing was written.',
        'bad',
        12000
      );
      return;
    }

    const parts: string[] = [];
    if (editor.changes.length) parts.push(`${editor.changes.length} field change(s)`);
    if (queue.length) parts.push(`${queue.length} inventory change(s)`);
    if (
      !(await confirmDanger(
        `Write ${parts.join(' and ')} to slot ${writeSlot}?\n\n` +
          (supported
            ? 'The original slot is held in a private rollback snapshot until the write and persistence finish.'
            : `The source slot ${slot} is unsupported and will remain untouched; the repaired save is written to an empty manual slot.`) +
          (needsEngine
            ? '\n\nRules-checked changes run inside the engine, which cannot hand back a playable session '
              + 'afterwards — the reactor restarts as soon as the write finishes, and anything unsaved '
              + 'in a game that is currently running is lost.'
            : '')
      ))
    )
      return;

    const source = snapshotSlot(FS, slot);
    const backup = snapshotSlot(FS, writeSlot);
    if (!source.length) {
      toast(`Backup failed, so nothing was written: slot ${slot} is empty.`, 'bad');
      return;
    }
    if (writeSlot !== slot) restoreSlotSnapshot(FS, writeSlot, source);

    // --- 1. the bridge round trip -------------------------------------------
    let engineWrote = false;
    const shortfalls: string[] = [];
    if (needsEngine && activeBridge) {
      const bridge = activeBridge;
      const rollback = async (message: string): Promise<void> => {
        let restored = false;
        try {
          restoreSlotSnapshot(FS, writeSlot, backup);
          restored = true;
          await host.persist();
          restarting = true;
          toast(`${message} Slot ${writeSlot} was restored and persisted. Restarting the reactor…`, 'bad', 9000);
          setTimeout(() => host.restart(), RESTART_DELAY_MS);
        } catch (e) {
          toast(
            `${message} ${restored ? `Slot ${writeSlot} was restored in memory, but persistence failed` : `Restoring slot ${writeSlot} also failed`} `
              + `(${(e as Error).message}). Do not reload before exporting the slot.`,
            'bad',
            15000
          );
        }
        render();
      };

      // The whole round trip, staged as one batch: load the slot, every queued
      // change in the order the player queued it, write the slot back. The
      // engine runs it inside its own loop and stops at the first failure.
      //
      // An exchange expands to four ops here rather than being a single engine
      // op, and the order is the engine lane's: unwield, remove, give, wield.
      // Wield requires the item to be in the top-level inventory already, so
      // the give has to come first — putting it last would fail with -1.
      const ops: BridgeOp[] = session.loadedBridge ? [] : [{ kind: 'load', slot: writeSlot }];
      /** Which queue entry each op belongs to, for attributing a shortfall. */
      const owner: number[] = ops.map(() => -1);
      ops.push(...engineOps);
      owner.push(...engineOps.map(() => -1));
      queue.forEach((entry, index) => {
        if (entry.op === 'exchange') {
          const expanded: BridgeOp[] = [
            { kind: 'unwield', equipSlot: entry.equipSlot },
            { kind: 'remove', pid: entry.fromPid, count: 1 },
            { kind: 'give', pid: entry.toPid, count: 1 },
            { kind: 'wield', pid: entry.toPid, equipSlot: entry.equipSlot },
          ];
          ops.push(...expanded);
          owner.push(...expanded.map(() => index));
        } else {
          ops.push(
            entry.op === 'give'
              ? { kind: 'give', owner: entry.owner, pid: entry.pid, count: entry.count }
              : { kind: 'remove', owner: entry.owner, pid: entry.pid, count: entry.count }
          );
          owner.push(index);
        }
      });
      ops.push({ kind: 'write', slot: writeSlot });
      owner.push(-1);

      // Before the batch is sent, not after it returns: a batch that fails
      // halfway has already torn down whatever the engine was holding, and the
      // engine does no rollback of its own.
      if (!session.loadedBridge) host.markEngineSpent();

      const outcome = await bridge.runBatch(ops);
      if (!outcome.ok) {
        await rollback(`${outcome.message}.`);
        return;
      }

      // A short remove is not a failure — the engine skips equipped items and
      // simply may not have had that many. `results` is positional, and `owner`
      // maps each result back to the change the player made.
      ops.forEach((op, i) => {
        if (op.kind !== 'remove') return;
        const got = outcome.results[i] ?? 0;
        if (got >= op.count) return;
        const entry = queue[owner[i] ?? -1];
        shortfalls.push(
          entry?.op === 'exchange'
            ? `exchange in the ${EQUIP_SLOT_NAMES[entry.equipSlot]}: the old item did not come out`
            : `pid ${op.pid}: removed ${got} of ${op.count}`
        );
      });
      engineWrote = true;
    }

    // --- 2. the scalar edits, on top of what the engine wrote ----------------
    try {
      if (engineWrote) {
        // Re-parsing is both the input to the replay and the check that the
        // engine wrote something this codec still understands. If it throws,
        // the slot goes back rather than being left half-understood.
        const fresh = parseSaveDat(
          FS.readFile(`${slotPath(writeSlot)}/SAVE.DAT`, { encoding: 'binary' }) as Uint8Array
        );
        if (editor.changes.length) {
          if (mode === 'engine') editor.replayAfterEngineWriteInto(fresh);
          else editor.replayInto(fresh);
          FS.writeFile(`${slotPath(writeSlot)}/SAVE.DAT`, serializeSaveDat(fresh));
        }
      } else {
        FS.writeFile(`${slotPath(writeSlot)}/SAVE.DAT`, serializeSaveDat(save));
      }
    } catch (e) {
      try {
        restoreSlotSnapshot(FS, writeSlot, backup);
        await host.persist();
      } catch {
        /* report below; the in-memory snapshot remains our last recovery source */
      }
      toast(
        `Write failed: ${(e as Error).message}. Slot ${writeSlot} was restored from its rollback snapshot.`,
        'bad',
        9000
      );
      if (engineWrote || engineWasClaimed) {
        restarting = true;
        setTimeout(() => host.restart(), RESTART_DELAY_MS);
      }
      render();
      return;
    }

    host.onMutate();
    if (shortfalls.length) toast(`Some removals came up short — ${shortfalls.join('; ')}.`, 'warn', 9000);

    // The panel is brought up to date before the filesystem flush, not after:
    // the write has landed in the engine's FS by this point, and leaving the
    // old change list on screen while IDBFS catches up reads as "nothing
    // happened".
    if (engineWrote) {
      queue.length = 0;
      engineOps.length = 0;
      restarting = true;
    } else if (engineWasClaimed) {
      restarting = true;
    } else {
      load(writeSlot);
    }
    render();
    // The engine syncs IDBFS itself at the end of rr_save_write, but the scalar
    // pass above is a shell-side write and a reload will not wait for it.
    try {
      await host.persist();
    } catch (e) {
      let rollbackPersisted = false;
      try {
        restoreSlotSnapshot(FS, writeSlot, backup);
        await host.persist();
        rollbackPersisted = true;
      } catch {
        /* report the original persistence error; the filesystem may be unavailable */
      }
      restarting = false;
      toast(
        rollbackPersisted
          ? `Persistence failed and slot ${writeSlot} was rolled back and persisted: ${(e as Error).message}`
          : `Persistence failed. Slot ${writeSlot} was restored in memory, but that rollback could not be persisted: ${(e as Error).message}`,
        'bad',
        12000,
      );
      load(slot);
      render();
      if (engineWrote || engineWasClaimed) {
        restarting = true;
        setTimeout(() => host.restart(), RESTART_DELAY_MS);
      }
      return;
    }
    toast(
      `Wrote and persisted slot ${writeSlot}.${writeSlot === slot ? '' : ` Source slot ${slot} was preserved.`}`
        + (engineWrote || engineWasClaimed ? ' Restarting the reactor…' : ''),
      'ok',
      engineWrote || engineWasClaimed ? RESTART_DELAY_MS : undefined
    );
    if (engineWrote || engineWasClaimed) setTimeout(() => host.restart(), RESTART_DELAY_MS);
  }

  // ------------------------------------------------------------ small pieces

  /** A picture-and-words card for a perk, trait or skill. */
  function cardFor(
    entry: ChargenEntry | undefined,
    fallbackName: string,
    description: string,
    rank?: number
  ): HTMLElement {
    const card = el('div', 'se-card');
    card.appendChild(artTile(session?.install ?? null, entry?.fid ?? 0, entry?.name ?? fallbackName, ICON_BOX, ICON_BOX));
    const text = el('div', 'se-card-text');
    const heading = el('div', 'se-card-name', entry?.named ? entry.name : fallbackName);
    if (rank !== undefined && rank > 1) heading.appendChild(el('span', 'se-item-count', `rank ${rank}`));
    text.appendChild(heading);
    if (description) {
      text.appendChild(el('p', 'se-card-desc', description));
    } else if (session?.installState === 'ready') {
      // Not a failure and not worth an alarm — the stock files leave a handful
      // of description slots empty and a mod can leave more.
      text.appendChild(el('p', 'se-card-desc hint', 'This install has no description text for it.'));
    }
    card.appendChild(text);
    return card;
  }

  /** Where the perk/trait text came from, for a card's source line. */
  function chargenSource(s: Session): string {
    return s.install?.chargen.sources.length
      ? `From this install's ${s.install.chargen.sources.join(', ')}.`
      : 'engine/src/trait.cc traitGetStatModifier';
  }

  /** Add this save's actual additive path to a derived-stat explanation. */
  function derivedValueExplanation(
    base: Explanation | null,
    formula: string,
    expectedBase: number,
    breakdown: DerivedDisplayBreakdown
  ): Explanation | null {
    if (!base) return null;
    const signed = (value: number) => value > 0 ? `+${value}` : String(value);
    const formulas: NonNullable<Explanation['formulas']> = [
      { label: 'Base rule', value: `${formula} = ${expectedBase}` },
    ];
    if (breakdown.storedBase !== expectedBase) {
      formulas.push({ label: 'Stored base', value: `${breakdown.storedBase} (does not match recompute)` });
    }
    if (breakdown.storedBonus !== 0) {
      formulas.push({ label: 'Stored bonus/effects', value: signed(breakdown.storedBonus) });
    }
    if (breakdown.traitModifier !== 0) {
      formulas.push({ label: 'Trait modifier', value: signed(breakdown.traitModifier) });
    }
    if (breakdown.readTimeModifier !== 0) {
      formulas.push({ label: 'Read-time perk', value: signed(breakdown.readTimeModifier) });
    }
    formulas.push({
      label: 'Shown total',
      value: breakdown.raw === breakdown.displayed
        ? String(breakdown.displayed)
        : `${breakdown.raw} → ${breakdown.displayed} (clamped)`,
    });
    return {
      ...base,
      formulas,
      detail: [
        'Shown total = stored base + stored bonus/effects + trait modifier + read-time modifiers, then the stat limit is applied. Perks and equipment often write into the stored bonus, so they are not always separate rows.',
        ...(base.detail ?? []),
      ],
    };
  }

  /** Explain one skill with this save's exact `skillGetValue` components. */
  function skillValueExplanation(
    name: string,
    breakdown: SkillValueBreakdown,
    entry: ChargenEntry | undefined
  ): Explanation {
    const use = skillUseExplanation(name) ?? SKILL_EXPLANATION;
    const signed = (value: number) => value > 0 ? `+${value}` : String(value);
    const formulas: NonNullable<Explanation['formulas']> = [
      { label: 'Starting value', value: String(breakdown.startingValue) },
    ];
    if (breakdown.statsAveraged && breakdown.statContributions.length === 2) {
      // Fallout 1 averages a two-stat skill rather than adding two terms
      // (`skill_level`, fallout1-ce skill.cc:186), so showing it as two rows
      // would be arithmetic the engine never does.
      const [first, second] = breakdown.statContributions as [
        (typeof breakdown.statContributions)[number],
        (typeof breakdown.statContributions)[number],
      ];
      formulas.push({
        label: `${humanLabel(STAT_NAMES[first.stat]!)} + ${humanLabel(STAT_NAMES[second.stat]!)}`,
        value: `(${first.statValue} + ${second.statValue}) × ${first.multiplier} ÷ 2 = ${signed(first.contribution)}`,
      });
    } else {
      for (const contribution of breakdown.statContributions) {
        const statName = humanLabel(STAT_NAMES[contribution.stat]!);
        formulas.push({
          label: statName,
          value: `${contribution.statValue} × ${contribution.multiplier} = ${signed(contribution.contribution)}`,
        });
      }
    }
    formulas.push({
      label: 'Stored points',
      value: `${Math.max(breakdown.rawPoints, 0)} × ${breakdown.pointsMultiplier} = ${signed(breakdown.pointsContribution)}`,
    });
    if (breakdown.tagged) {
      formulas.push({ label: 'Tagged-point copy', value: signed(breakdown.tagPointsContribution) });
      if (breakdown.tagFlatBonus !== 0) formulas.push({ label: 'Tag bonus', value: signed(breakdown.tagFlatBonus) });
    }
    if (breakdown.traitModifier !== 0) formulas.push({ label: 'Traits', value: signed(breakdown.traitModifier) });
    if (breakdown.perkModifier !== 0) formulas.push({ label: 'Perks', value: signed(breakdown.perkModifier) });
    if (breakdown.difficultyModifier !== 0) formulas.push({ label: 'Difficulty', value: signed(breakdown.difficultyModifier) });
    if (breakdown.negativePointsAdjustment !== 0) {
      formulas.push({ label: 'Negative-point adjustment', value: signed(breakdown.negativePointsAdjustment) });
    }
    formulas.push({ label: 'Uncapped total', value: `${breakdown.uncapped}%` });
    formulas.push({
      label: 'Shown total',
      value: breakdown.capped ? `${breakdown.value}% (clamped)` : `${breakdown.value}%`,
    });

    const detail = [...(use.detail ?? [])];
    if (entry?.description) detail.push(`The game’s description: “${entry.description}”`);
    return {
      summary: use.summary,
      formulas,
      detail,
      source: [
        'engine/src/skill.cc skillGetValue',
        use.source,
        entry?.description && session ? chargenSource(session) : '',
      ].filter(Boolean).join('; '),
    };
  }

  /**
   * The install's own words plus ours. The game's description says what the
   * thing is for; ours says which numbers it moves. Neither replaces the other,
   * and the source line names both.
   */
  function mergeExplanation(base: Explanation | null, entry: ChargenEntry | undefined): Explanation | null {
    if (!base) {
      return entry?.description
        ? { summary: entry.description, source: session ? chargenSource(session) : 'the install' }
        : null;
    }
    if (!entry?.description) return base;
    return {
      summary: base.summary,
      detail: [...(base.detail ?? []), `The game's own description: “${entry.description}”`],
      source: base.source,
    };
  }

  const api: SaveEditorPanel = {
    get isOpen() {
      return overlay.classList.contains('open');
    },
    open(slot: number) {
      tab = 'Character';
      mode = 'engine';
      // The engine may have moved on since the panel was last open.
      liveness = 'unknown';
      load(slot);
      render();
      overlay.classList.add('open');
    },
    close() {
      overlay.classList.remove('open');
      session = null;
      failure = null;
    },
    // Edits live in memory until Write, so closing throws them away. Say so.
    // Queued inventory operations live in memory too and are just as lost.
    requestClose() {
      // Nothing to save and nothing to close on to: the reload owns the page.
      if (restarting) return;
      if (inventoryLoading) {
        toast('Party inventories are still loading. Wait for the engine to finish before closing.', 'warn');
        return;
      }
      const pending = (session?.editor.changes.length ?? 0)
        + (session?.queue.length ?? 0)
        + (session?.engineOps.length ?? 0);
      // The confirm is the shell's own async dialog now, and requestClose is a
      // sync interface method — so the decision runs as a detached task. The
      // dialog is modal, so nothing else races the close it may perform.
      void (async () => {
        if (
          pending &&
          !(await confirmDanger(`${pending} change(s) have not been written to slot ${session?.slot}. Discard them?`))
        )
          return;
        if (session?.engineClaimed) {
          restarting = true;
          toast('Restarting the reactor to leave the loaded save-editor session…', 'ok', RESTART_DELAY_MS);
          setTimeout(() => host.restart(), RESTART_DELAY_MS);
        } else {
          api.close();
        }
      })();
    },
  };

  $('#btn-saveedit-close').addEventListener('click', () => api.requestClose());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) api.requestClose();
  });

  return api;
}

// ------------------------------------------------------------- free functions

/**
 * The globals whose meaning has actually been established, and what the values
 * are known to mean. Two entries, and it stays that way unless something is
 * genuinely verified: the whole point is that an unnamed flag says so rather
 * than being decorated with a plausible guess.
 */
const VERIFIED_GVAR_MEANINGS: ReadonlyMap<number, string> = new Map([
  [
    10,
    'Temple of Trials master flag, decoded from the Arroyo scripts: 0 not started, 1 mid-trial, '
      + '2 passed Cameron, 3 done and rewarded. Klint, Cameron and the opening cutscene all branch on it.',
  ],
]);

/** One requirement line, in the words the picker shows. */
function requirementText(requirement: PerkRequirement): string {
  switch (requirement.kind) {
    case 'not-selectable':
      return 'The game never offers this perk in the level-up dialog — scripts and items hand it out.';
    case 'level':
      return `Level ${requirement.needed} (you are ${requirement.have})`;
    case 'rank':
      return `Rank ${requirement.have} of ${requirement.needed} taken`;
    case 'perk-limit':
      return `${requirement.have} of ${requirement.needed} perks held`;
    case 'stat': {
      const name = STAT_NAMES[requirement.stat] ?? `stat ${requirement.stat}`;
      return requirement.below
        ? `${name} below ${requirement.needed} (you have ${requirement.have})`
        : `${name} ${requirement.needed} (you have ${requirement.have})`;
    }
    case 'skill': {
      const name = SKILL_NAMES[requirement.skill] ?? `skill ${requirement.skill}`;
      return requirement.below
        ? `${name} below ${requirement.needed}% (you have ${requirement.have}%)`
        : `${name} ${requirement.needed}% (you have ${requirement.have}%)`;
    }
    case 'gvar':
      return requirement.below
        ? `game global #${requirement.gvar} below ${requirement.needed} (it is ${requirement.have})`
        : `game global #${requirement.gvar} at least ${requirement.needed} (it is ${requirement.have})`;
  }
}

/**
 * Expert mode's list: every perk the file can hold, availability annotated but
 * not enforced.
 *
 * It goes through the same `perkAvailability` engine mode uses, so the two lists
 * cannot disagree about what a requirement is — they disagree only about what to
 * do when one is unmet.
 */
function allPerksForExpert(save: SaveDat): PerkAvailability[] {
  const ctx = perkContextFor(save);
  const out: PerkAvailability[] = [];
  for (let perk = 0; perk < forGame(save.game).perkCount; perk++) out.push(perkAvailability(perk, ctx));
  return out;
}

/** `slots` from `stackInventory` are words; the bridge wants a slot number. */
function slotFromName(name: string): EquipSlot | null {
  const lower = name.toLowerCase();
  if (lower.includes('left')) return EQUIP_SLOT_LEFT_HAND;
  if (lower.includes('right')) return EQUIP_SLOT_RIGHT_HAND;
  if (lower.includes('worn') || lower.includes('armor') || lower.includes('armour')) return EQUIP_SLOT_ARMOR;
  return null;
}

function matchesSlot(name: string, wanted: string): boolean {
  return slotFromName(name) === slotFromName(wanted);
}

/**
 * `CSS.escape` where it exists. Field keys are `stat:STRENGTH` and friends —
 * the colon is a selector metacharacter, so it has to be escaped before it goes
 * into `querySelectorAll`.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}
